import { createElement, useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { keepPreviousData, useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ToastAction, type ToastActionElement } from "@/components/ui/toast";
import { apiClient } from "@/lib/api/client";
import { hasWorkspaceInQuery } from "@/lib/api/workspaceScopedEndpoints";
import { ApiError } from "@/lib/api";
import { isMetaRateLimitError, retryAfterSecondsFrom, shouldRetryMetaQuery } from "@/lib/api/metaRateLimit";
import { useToast } from "@/hooks/use-toast";
import { showApiError } from "@/lib/errors";
import { getAiAnalyticsUrl, getAiAnalyticsBaseUrl } from "@/lib/api/aiAnalyticsUrl";
import { getOrganizationId } from "@/lib/api/organization";
import { useBusinessAccountContext } from "@/hooks/useBusinessAccountContext";
import { useWorkspaceContext } from "@/contexts/WorkspaceContext";
import { isPinnedAccountQuery, sameAdAccount, syncPinFor, type AdsSyncPin } from "@/lib/ads/adsSyncPin";
import { errorCodeOf, pinKey, runPinnedSync, shouldAutoImport, type SyncRoundResult, type SyncRunResult } from "@/lib/ads/adsSyncRunner";
import { putToS3, uploadAdVideoToS3 } from "@/lib/ads/adVideoUpload";
import { gatedFileContentType } from "@/lib/ads/leadForm";
import type { FieldCapabilities } from "@/lib/ads/editCapabilities";
import type { CampaignEditPayload } from "@/lib/ads/campaignEdit";
import type { AdSetEditPayload } from "@/lib/ads/adSetEdit";
import type { AdCreativeChanges } from "@/lib/ads/adCreativeEdit";
import { refreshNotice, stampRefreshAvailability, type DatasetQualityView } from "@/lib/ads/datasetQuality";
import { formsRefreshNotice, stampFormsRefresh, type InstantFormsView } from "@/lib/ads/instantForms";
import type { ValueRuleSetsResponse, ValueRuleSetView } from "@/lib/ads/valueRules";
import {
  VIDEO_POLL_INTERVAL_MS,
  isTerminalVideoStatus,
  videoPollExpired,
  videoPollState,
  videoProblemMessage,
} from "@/lib/ads/videoStatus";
import {
  bareAdAccountId,
  disconnectBlockedMessage,
  leadRecoverySummary,
  summarizeDisconnectResult,
  type BusinessLoginConnectResult,
  type DisconnectPreview,
  type DisconnectResult,
  type LoginAdAccountsResult,
  type MetaAdsOAuthPurpose,
} from "@/lib/ads/adAccounts";
import {
  AD_ACCOUNT_HEADER,
  clearAdAccountSelection,
  getSelectedAdAccountId,
  handleAdAccountNotConnected,
  isSelectionRejection,
  reconcileAdAccountSelection,
  selectAdAccount,
  statusForSelection,
  subscribeAdAccountSelection,
} from "@/lib/ads/adAccountSelection";
import type { MetaAdNode, MetaAdSetNode, MetaCampaignNode, MetaCreativeNode } from "@/lib/ads/metaGraph";
import type { AccountResult, OverviewAccountRef } from "@/lib/ads/overviewAllAccounts";

const ADS_API = "/ads";

const WORKSPACE_LEVEL_ADS_KEYS = new Set(["setup-status", "overview-accounts", "all-accounts"]);

export function resetAdAccountScopedQueries(queryClient: QueryClient) {
  void queryClient.resetQueries({
    queryKey: ["ads"],
    predicate: (query) => !WORKSPACE_LEVEL_ADS_KEYS.has(String(query.queryKey[1])),
  });
  void queryClient.resetQueries({ queryKey: ["instagram-ads"] });
  void queryClient.resetQueries({ queryKey: ["ad-experiments"] });
  void queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
}

const selectionBoundClients = new Map<QueryClient, number>();
let stopSelectionResets: (() => void) | null = null;

export function bindAdAccountSelectionResets(queryClient: QueryClient): () => void {
  selectionBoundClients.set(queryClient, (selectionBoundClients.get(queryClient) ?? 0) + 1);
  if (!stopSelectionResets) {
    stopSelectionResets = subscribeAdAccountSelection((change) => {
      if (!change.resetCaches) return;
      for (const client of Array.from(selectionBoundClients.keys())) {
        resetAdAccountScopedQueries(client);
      }
    });
  }
  return () => {
    const count = (selectionBoundClients.get(queryClient) ?? 1) - 1;
    if (count > 0) selectionBoundClients.set(queryClient, count);
    else selectionBoundClients.delete(queryClient);
    if (selectionBoundClients.size === 0 && stopSelectionResets) {
      stopSelectionResets();
      stopSelectionResets = null;
    }
  };
}

const subscribeSelection = (onChange: () => void) => subscribeAdAccountSelection(() => onChange());

export function useSelectedAdAccountId(): string | null {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  return useSyncExternalStore(
    subscribeSelection,
    () => getSelectedAdAccountId(workspaceId),
    () => null,
  );
}

export function useAdAccountSelection() {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const setupStatus = useAdsSetupStatus();
  const selectedId = useSelectedAdAccountId();
  const data = setupStatus.data as
    | { account?: { ad_account_id?: string | null } | null; default_ad_account_id?: string | null; accounts?: Array<{ ad_account_id?: string | null; is_default?: boolean | null }> }
    | undefined;
  const defaultId =
    bareAdAccountId(
      data?.default_ad_account_id ??
        data?.accounts?.find((row) => row?.is_default)?.ad_account_id ??
        "",
    ) || null;
  const effectiveId = selectedId ?? (bareAdAccountId(data?.account?.ad_account_id) || null);
  const select = useCallback(
    (adAccountId: string) => selectAdAccount(workspaceId, adAccountId),
    [workspaceId],
  );
  const clear = useCallback(() => clearAdAccountSelection(workspaceId), [workspaceId]);
  return { workspaceId, selectedId, effectiveId, defaultId, select, clear };
}

function adsCurrentWorkspaceId(): string | null {
  try {
    return localStorage.getItem("selectedWorkspaceId") || null;
  } catch {
    return null;
  }
}

async function postAdsMultipart(route: string, formData: FormData, failure: string) {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
  const token = localStorage.getItem("auth_token");
  const ws = adsCurrentWorkspaceId();
  if (!ws) throw new ApiError({ error: "Pick a workspace before uploading.", message: "Pick a workspace before uploading." });
  const adAccountId = getSelectedAdAccountId(ws);
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (adAccountId) headers[AD_ACCOUNT_HEADER] = adAccountId;
  let res: Response;
  try {
    res = await fetch(`${baseUrl}${ADS_API}/${route}?workspace_id=${encodeURIComponent(ws)}`, {
      method: "POST",
      headers,
      body: formData,
    });
  } catch {
    throw new ApiError({ error: `${failure}: could not reach the server.`, message: `${failure}: could not reach the server.` });
  }
  const json: any = await res.json().catch(() => null);
  if (!res.ok || !json) {
    if (adAccountId && isSelectionRejection(json, adAccountId)) {
      handleAdAccountNotConnected(ws, adAccountId);
    }
    if (res.status === 413) {
      throw new ApiError({ error: "This file is too large to upload.", message: "This file is too large to upload." });
    }
    throw ApiError.fromResult(json, `${failure} (${res.status})`);
  }
  return { data: json.data, error: null, success: true };
}

const adsRequest = {
  get: (path: string, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    if (!ws || hasWorkspaceInQuery(path)) return apiClient.get(path, opts);
    if (opts?.params) {
      return apiClient.get(path, { ...opts, params: { workspace_id: ws, ...opts.params } });
    }
    const sep = path.includes("?") ? "&" : "?";
    return apiClient.get(`${path}${sep}workspace_id=${encodeURIComponent(ws)}`, opts);
  },
  post: (path: string, data?: any, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    const body =
      ws && data && typeof data === "object" && !Array.isArray(data)
        ? { workspace_id: ws, ...data }
        : data;
    return apiClient.post(path, body, opts);
  },
  delete: (path: string, opts?: any) => {
    const ws = adsCurrentWorkspaceId();
    if (!ws) return apiClient.delete(path, opts);
    const sep = path.includes("?") ? "&" : "?";
    return apiClient.delete(`${path}${sep}workspace_id=${encodeURIComponent(ws)}`, opts);
  },
};

export type CrmStageMappingMode = "inherit" | "muted" | "event";

export interface CrmFunnelEvent {
  name: string;
  label: string;
  description: string;
  order: number;
  negative: boolean;
}

export interface CrmStageMappingItem {
  board_stage_id: string;
  stage_name: string;
  board_id: string;
  board_name: string;
  position: number;
  event_name: string | null;
  source: "explicit" | "muted" | "inherit" | "invalid";
  inherited_event: string | null;
  mode: CrmStageMappingMode;
  board_contact_count?: number | null;
  board_meta_lead_count?: number | null;
}

export interface CrmStageMappingInput {
  board_stage_id: string;
  mode: CrmStageMappingMode;
  event_name?: string | null;
}

export interface CrmOptimizingAdSet {
  event_name: string;
  adsets: Array<{
    id: string;
    name: string;
    status: string;
    pixel_id?: string | null;
    ad_account_id?: string | null;
    account_dataset_id?: string | null;
  }>;
  mappable?: boolean;
  targets_other_dataset?: boolean;
}

export interface CrmPixel {
  id: string;
  name: string | null;
  last_fired_time: string | null;
  creation_time: string | null;
  is_unavailable: boolean;
}

export interface CrmDatasetView {
  selected_dataset_id: string | null;
  selected_source: "explicit" | "fallback" | "none";
  selected_is_known: boolean;
  pixel_id: string | null;
  crm_dataset_id: string | null;
  ad_account_id: string;
  ad_account_name: string | null;
  pixels: CrmPixel[];
  warning: string | null;
  can_edit: boolean;
}

export interface RawLeadEventAccount {
  ad_account_id: string;
  ad_account_name: string | null;
  dataset_id: string | null;
  enabled: boolean;
  received_30d: number | null;
}

export interface RawLeadEventView {
  event_name: string;
  accounts: RawLeadEventAccount[];
}

export interface CrmStageMappingView {
  items: CrmStageMappingItem[];
  events: CrmFunnelEvent[];
  orphan_mapping_count: number;
  optimizing: CrmOptimizingAdSet[];
  unmatched_optimizations: CrmOptimizingAdSet[];
  received_counts: Record<string, number> | null;
  received_by_dataset?: Record<string, Record<string, number>>;
  dataset_names?: Record<string, string>;
  dataset_id: string | null;
  insights_warning: string | null;
  can_edit: boolean;
  raw_lead_event?: RawLeadEventView | null;
}

export type MetaEditLevel = "campaign" | "adset" | "ad";

const META_EDIT_PATH: Record<MetaEditLevel, string> = {
  campaign: "meta-campaigns",
  adset: "meta-adsets",
  ad: "meta-ads",
};

export interface MetaEditContext {
  level: MetaEditLevel;
  object: MetaCampaignNode & MetaAdSetNode & MetaAdNode;
  campaign: MetaCampaignNode | null;
  adset: MetaAdSetNode | null;
  creative: MetaCreativeNode | null;
  adsets?: MetaAdSetNode[];
  ad_destination?: string | null;
  ad_destination_error?: string | null;
  new_adset_destination?: string | null;
  new_adset_destination_error?: string | null;
  new_adset_value_rules_blocked?: string | null;
  capabilities: FieldCapabilities;
  account: { ad_account_id: string; currency: string | null; page_id: string | null };
}

export type AdsInsightsWindow = { datePreset?: string; since?: string; until?: string };

function insightsWindowOf(win: string | AdsInsightsWindow | undefined): AdsInsightsWindow {
  if (typeof win === "string") return { datePreset: win };
  if (win?.since && win?.until) return { since: win.since, until: win.until };
  return { datePreset: win?.datePreset || "last_30d" };
}

function insightsWindowParams(win: AdsInsightsWindow): Record<string, string> | undefined {
  if (win.since && win.until) return { since: win.since, until: win.until };
  return win.datePreset ? { date_preset: win.datePreset } : undefined;
}

const adsApi = {
  getSetupStatus: (businessAccountId?: string | null, workspaceId?: string | null) => {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.set("business_account_id", businessAccountId);
    if (workspaceId) qs.set("workspace_id", workspaceId);
    const q = qs.toString();
    return adsRequest.get(`${ADS_API}/setup/status${q ? `?${q}` : ""}`);
  },
  getOAuthUrl: (purpose?: MetaAdsOAuthPurpose) =>
    adsRequest.get(`${ADS_API}/setup/oauth-url${purpose ? `?purpose=${purpose}` : ""}`),
  handleCallback: (code: string) => adsRequest.post(`${ADS_API}/setup/callback`, { code }),
  listAdAccountsFromLogin: (code: string, state: string) =>
    adsRequest.post(`${ADS_API}/setup/login-ad-accounts`, { code, state }),
  connectFromLogin: (login: string, adAccountIds: string[]) =>
    adsRequest.post(`${ADS_API}/setup/connect-from-login`, { login, ad_account_ids: adAccountIds }),
  connectAdAccount: (data: any) => adsRequest.post(`${ADS_API}/setup/connect`, data),
  getBalance: (businessAccountId?: string | null) =>
    adsRequest.get(
      `${ADS_API}/setup/balance${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getAvailableAdAccounts: (opts?: { includeBusinessScoped?: boolean }) =>
    adsRequest.get(
      `${ADS_API}/setup/ad-accounts${
        opts?.includeBusinessScoped ? "?include_business_scoped=true" : ""
      }`
    ),
  switchAdAccount: (data: any) => adsRequest.post(`${ADS_API}/setup/switch`, data),
  setDefaultAdAccount: (adAccountId: string) =>
    apiClient.patch(`${ADS_API}/setup/default-account`, { ad_account_id: adAccountId }),
  getAccountsOverview: (win: { datePreset?: string; since?: string; until?: string } = {}) => {
    const params: Record<string, string> = {};
    if (win.since && win.until) {
      params.since = win.since;
      params.until = win.until;
    } else if (win.datePreset) {
      params.date_preset = win.datePreset;
    }
    return adsRequest.get(
      `${ADS_API}/overview/accounts`,
      Object.keys(params).length ? { params } : undefined,
    );
  },
  syncNow: (pin?: { workspaceId: string; adAccountId: string }) =>
    pin
      ? adsRequest.post(`${ADS_API}/sync`, { workspace_id: pin.workspaceId }, { headers: { [AD_ACCOUNT_HEADER]: pin.adAccountId } })
      : adsRequest.post(`${ADS_API}/sync`, {}),
  getSyncStatus: (pin?: { workspaceId: string; adAccountId: string }) =>
    pin
      ? adsRequest.get(`${ADS_API}/sync/status`, { params: { workspace_id: pin.workspaceId }, headers: { [AD_ACCOUNT_HEADER]: pin.adAccountId } })
      : adsRequest.get(`${ADS_API}/sync/status`),
  getCrmDataset: (businessAccountId?: string | null) =>
    adsRequest.get(
      businessAccountId
        ? `${ADS_API}/crm-dataset?business_account_id=${encodeURIComponent(businessAccountId)}`
        : `${ADS_API}/crm-dataset`
    ),
  getDatasetEvents: (businessAccountId?: string | null, datasetId?: string | null) => {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.set("business_account_id", businessAccountId);
    if (datasetId) qs.set("dataset_id", datasetId);
    const q = qs.toString();
    return adsRequest.get(`${ADS_API}/dataset-events${q ? `?${q}` : ""}`);
  },
  getInstantForms: (businessAccountId?: string | null, refresh = false) => {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.set("business_account_id", businessAccountId);
    if (refresh) qs.set("refresh", "1");
    const q = qs.toString();
    return adsRequest.get(`${ADS_API}/instant-forms${q ? `?${q}` : ""}`);
  },
  getValueRuleSets: (businessAccountId?: string | null) =>
    adsRequest.get(
      businessAccountId
        ? `${ADS_API}/value-rule-sets?business_account_id=${encodeURIComponent(businessAccountId)}`
        : `${ADS_API}/value-rule-sets`,
    ),
  createValueRuleSet: (body: Record<string, unknown>) => adsRequest.post(`${ADS_API}/value-rule-sets`, body),
  getDatasetQuality: (businessAccountId?: string | null, refresh = false) => {
    const qs = new URLSearchParams();
    if (businessAccountId) qs.set("business_account_id", businessAccountId);
    if (refresh) qs.set("refresh", "1");
    const q = qs.toString();
    return adsRequest.get(`${ADS_API}/dataset-quality${q ? `?${q}` : ""}`);
  },
  setCrmDataset: (businessAccountId: string, crmDatasetId: string | null) =>
    apiClient.patch(`${ADS_API}/setup/lead-crm-settings`, {
      business_account_id: businessAccountId,
      crm_dataset_id: crmDatasetId,
    }),
  getCrmStageMapping: (businessAccountId?: string | null) =>
    adsRequest.get(
      businessAccountId
        ? `${ADS_API}/crm-stage-mapping?business_account_id=${encodeURIComponent(businessAccountId)}`
        : `${ADS_API}/crm-stage-mapping`
    ),
  updateCrmStageMapping: (businessAccountId: string, mappings: CrmStageMappingInput[]) =>
    apiClient.put(`${ADS_API}/crm-stage-mapping`, {
      business_account_id: businessAccountId,
      mappings,
    }),
  setRawLeadEvent: (adAccountId: string, enabled: boolean) =>
    apiClient.patch(`${ADS_API}/setup/raw-lead-event`, {
      ad_account_id: adAccountId,
      enabled,
    }),
  disconnect: (
    adAccountId?: string,
    businessAccountId?: string | null,
    pauseCampaigns?: boolean
  ) => {
    const params = new URLSearchParams();
    if (adAccountId) params.set("ad_account_id", adAccountId);
    if (businessAccountId) params.set("business_account_id", businessAccountId);
    if (typeof pauseCampaigns === "boolean") params.set("pause_campaigns", String(pauseCampaigns));
    const qs = params.toString();
    return adsRequest.delete(`${ADS_API}/setup/disconnect${qs ? `?${qs}` : ""}`);
  },
  getDisconnectPreview: (adAccountId: string) =>
    adsRequest.get(`${ADS_API}/setup/disconnect-preview`, {
      params: { ad_account_id: adAccountId },
    }),
  syncLeads: (body: { ad_account_id?: string } = {}) =>
    adsRequest.post(`${ADS_API}/leads/sync`, body),

  getCampaigns: (params: any) => adsRequest.get(`${ADS_API}/campaigns`, { params }),
  createCampaign: (data: any) => adsRequest.post(`${ADS_API}/campaigns`, data),
  listCampaignDrafts: () => adsRequest.get(`${ADS_API}/campaign-drafts`),
  getCampaignDraft: (draftId: string) => adsRequest.get(`${ADS_API}/campaign-drafts/${encodeURIComponent(draftId)}`),
  saveCampaignDraft: (data: any) => adsRequest.post(`${ADS_API}/campaign-drafts`, data),
  deleteCampaignDraft: (draftId: string) => adsRequest.delete(`${ADS_API}/campaign-drafts/${encodeURIComponent(draftId)}`),

  createAdFromPost: (data: any) => adsRequest.post(`${ADS_API}/from-post`, data),
  recommendAd: (data: any) => adsRequest.post(`${ADS_API}/recommend`, data),

  getAttribution: (params?: { from?: string; to?: string }) =>
    adsRequest.get(`${ADS_API}/attribution`, params ? { params } : undefined),

  getLeadQuality: (params?: { from?: string; to?: string }) =>
    adsRequest.get(`${ADS_API}/lead-quality`, params ? { params } : undefined),

  getLeadQualityStages: (params: {
    ad_id?: string;
    ad_name?: string;
    from?: string;
    to?: string;
  }) => adsRequest.get(`${ADS_API}/lead-quality/stages`, { params }),

  getAdRankings: (params: { date_preset?: string; since?: string; until?: string; ad_ids?: string }) =>
    adsRequest.get(`${ADS_API}/rankings`, { params }),

  suggestFromPost: (data: { post: Record<string, any>; baseline?: Record<string, any>; business_account_id?: string }) =>
    adsRequest.post(`${ADS_API}/suggest-from-post`, data),
  getSuggestions: () => adsRequest.get(`${ADS_API}/suggestions`),
  dismissSuggestion: (id: string) => adsRequest.post(`${ADS_API}/suggestions/${id}/dismiss`),
  getCampaign: (id: string) => adsRequest.get(`${ADS_API}/campaigns/${id}`),
  updateCampaign: (id: string, data: any) => apiClient.patch(`${ADS_API}/campaigns/${id}`, data),
  deleteCampaign: (id: string) => adsRequest.delete(`${ADS_API}/campaigns/${id}`),
  syncCampaign: (id: string) => adsRequest.post(`${ADS_API}/campaigns/${id}/sync`),
  duplicateCampaign: (id: string) => adsRequest.post(`${ADS_API}/campaigns/${id}/duplicate`),

  getCampaignInsights: (id: string, params: any) =>
    adsRequest.get(`${ADS_API}/campaigns/${id}/insights`, { params }),
  getLeadsChart: (id: string, params: any) =>
    adsRequest.get(`${ADS_API}/campaigns/${id}/leads-chart`, { params }),

  searchInterests: (query: string) => adsRequest.post(`${ADS_API}/search/interests`, { query }),
  searchLocations: (query: string, mode?: string) =>
    adsRequest.post(`${ADS_API}/search/locations`, mode ? { query, mode } : { query }),
  searchBehaviors: (query: string) => adsRequest.post(`${ADS_API}/search/behaviors`, { query }),
  getConnectedInstagram: () => adsRequest.get(`${ADS_API}/setup/instagram`),
  getCplEstimate: (params: { objective?: string; daily_budget?: number }) =>
    adsRequest.get(`${ADS_API}/cpl-estimate`, { params }),
  getPlatformBreakdown: (win: { datePreset?: string; since?: string; until?: string } = {}) => {
    const params: Record<string, string> = {};
    if (win.since && win.until) {
      params.since = win.since;
      params.until = win.until;
    } else if (win.datePreset) {
      params.date_preset = win.datePreset;
    }
    return adsRequest.get(
      `${ADS_API}/insights/platforms`,
      Object.keys(params).length ? { params } : undefined,
    );
  },
  getAccountReach: (win: { datePreset?: string; since?: string; until?: string } = {}) => {
    const params: Record<string, string> = {};
    if (win.since && win.until) {
      params.since = win.since;
      params.until = win.until;
    } else if (win.datePreset) {
      params.date_preset = win.datePreset;
    }
    return adsRequest.get(`${ADS_API}/insights/reach`, Object.keys(params).length ? { params } : undefined);
  },
  searchJobTitles: (query: string) => adsRequest.post(`${ADS_API}/search/job-titles`, { query }),
  browseTargeting: (cls: string, query?: string) =>
    adsRequest.post(`${ADS_API}/targeting/browse`, query ? { class: cls, query } : { class: cls }),
  searchAllTargeting: (query: string) => adsRequest.post(`${ADS_API}/targeting/search`, { query }),
  getTargetingEnums: () => adsRequest.get(`${ADS_API}/targeting/enums`),
  estimateAudience: (targetingSpec: unknown, optimizationGoal?: string) =>
    adsRequest.post(`${ADS_API}/targeting/estimate`, {
      targeting_spec: targetingSpec,
      ...(optimizationGoal ? { optimization_goal: optimizationGoal } : {}),
    }),

  getAudiences: () => adsRequest.get(`${ADS_API}/audiences`),
  createCustomAudience: (data: any) => adsRequest.post(`${ADS_API}/audiences/custom`, data),
  createAudienceFromContacts: (data: any) =>
    adsRequest.post(`${ADS_API}/audiences/from-contacts`, data),
  createLookalikeAudience: (data: any) => adsRequest.post(`${ADS_API}/audiences/lookalike`, data),
  getAudiencePresets: () => adsRequest.get(`${ADS_API}/audiences/presets`),
  saveAudiencePreset: (data: any) => adsRequest.post(`${ADS_API}/audiences/presets`, data),

  getFlows: () => adsRequest.get(`${ADS_API}/flows`),

  fetchAdsFromMeta: (
    datePreset?: string,
    range?: { since: string; until: string }
  ) => {
    const params: Record<string, string> = {};
    if (datePreset) params.date_preset = datePreset;
    if (range?.since && range?.until) {
      params.since = range.since;
      params.until = range.until;
    }
    return adsRequest.get(
      `${ADS_API}/fetch-from-meta`,
      Object.keys(params).length ? { params } : undefined
    );
  },
  getMultiAccountSummary: (
    adAccountIds: string[],
    win: { datePreset?: string; since?: string; until?: string } = {},
  ) =>
    adsRequest.post(`${ADS_API}/multi-account-summary`, {
      ad_account_ids: adAccountIds,
      date_preset: win.datePreset,
      since: win.since,
      until: win.until,
    }),
  getBreakdown: (dimension: string, datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/breakdown`, {
      params: { dimension, ...(datePreset ? { date_preset: datePreset } : {}) },
    }),
  getFunnel: (win: AdsInsightsWindow = {}) => {
    const params = insightsWindowParams(win);
    return adsRequest.get(`${ADS_API}/insights/funnel`, params ? { params } : undefined);
  },
  getAnomalies: () => adsRequest.get(`${ADS_API}/insights/anomalies`),
  getWeeklyDigest: () => adsRequest.get(`${ADS_API}/insights/weekly-digest`),
  buildCustomReport: (body: {
    level: string; metrics: string[]; sortBy?: string; sortDir?: string; date_preset?: string;
  }) => adsRequest.post(`${ADS_API}/insights/custom-report`, body),
  getCreativeInsights: (datePreset?: string) =>
    adsRequest.get(`${ADS_API}/insights/creative`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getSpendPacing: () => adsRequest.get(`${ADS_API}/insights/pacing`),
  getRevenuePerLead: (win: AdsInsightsWindow = {}) => {
    const params = insightsWindowParams(win);
    return adsRequest.get(`${ADS_API}/insights/revenue-per-lead`, params ? { params } : undefined);
  },

  updateMetaAd: (metaAdId: string, data: any) => adsRequest.post(`${ADS_API}/meta-ads/${metaAdId}`, data),
  updateMetaCampaign: (metaCampaignId: string, data: any) => adsRequest.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  uploadImage: (imageUrl: string) => adsRequest.post(`${ADS_API}/upload-image`, { image_url: imageUrl }),
  uploadImageFile: (formData: FormData) => postAdsMultipart("upload-image-file", formData, "Image upload failed"),
  uploadVideoFile: (formData: FormData) => postAdsMultipart("upload-video-file", formData, "Video upload failed"),

  generateAdCopy: (data: any) => adsRequest.post(`${ADS_API}/generate-copy`, data),

  updateMetaAdSet: (metaAdSetId: string, data: any) => adsRequest.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  getMetaAdDetail: (metaAdId: string) => adsRequest.get(`${ADS_API}/meta-ads/${metaAdId}`),

  getMetaCampaignDetail: (metaCampaignId: string) =>
    adsRequest.get(`${ADS_API}/meta-campaigns/${metaCampaignId}/detail`),

  getMetaAdSetDetail: (metaAdSetId: string) =>
    adsRequest.get(`${ADS_API}/meta-adsets/${metaAdSetId}/detail`),

  getAdLocales: () => adsRequest.get(`${ADS_API}/targeting/locales`),

  getMetaEditContext: (level: MetaEditLevel, metaId: string) =>
    adsRequest.get(`${ADS_API}/${META_EDIT_PATH[level]}/${metaId}/edit-context`),

  getMetaAdInsights: (metaAdId: string, params: any) =>
    adsRequest.get(`${ADS_API}/meta-ads/${metaAdId}/insights`, { params }),

  getMetaCampaignInsights: (metaCampaignId: string, params: any) =>
    adsRequest.get(`${ADS_API}/meta-campaigns/${metaCampaignId}/insights`, { params }),

  getMetaAdSetInsights: (metaAdSetId: string, params: any) =>
    adsRequest.get(`${ADS_API}/meta-adsets/${metaAdSetId}/insights`, { params }),

  createAdSetInCampaign: (metaCampaignId: string, data: any) =>
    adsRequest.post(`${ADS_API}/meta-campaigns/${metaCampaignId}/adsets`, data),

  createAdInAdSet: (metaAdSetId: string, data: any) =>
    adsRequest.post(`${ADS_API}/meta-adsets/${metaAdSetId}/ads`, data),

  aiAssistant: (data: any) => adsRequest.post(`${ADS_API}/ai-assistant`, data),

  getBusinesses: () => adsRequest.get(`${ADS_API}/businesses`),
  createAdAccount: (data: any) => adsRequest.post(`${ADS_API}/create-ad-account`, data),
  getFundingDetails: () => adsRequest.get(`${ADS_API}/funding`),

  getSuppressionStatus: () => adsRequest.get(`${ADS_API}/setup/suppression/status`),
  enableSuppression: () => adsRequest.post(`${ADS_API}/setup/suppression/enable`, {}),
  applySuppressionToActive: () =>
    adsRequest.post(`${ADS_API}/setup/suppression/apply-to-active`, {}),
  backfillSuppression: () => adsRequest.post(`${ADS_API}/setup/suppression/backfill`, {}),

  createProductCatalog: (data: { business_id: string; name: string; vertical?: string }) =>
    adsRequest.post(`${ADS_API}/catalogs`, data),
  addProductToCatalog: (catalogId: string, product: any) =>
    adsRequest.post(`${ADS_API}/catalogs/${catalogId}/products`, product),
  getProductCatalogs: () => adsRequest.get(`${ADS_API}/catalogs`),
  getCatalogProductSets: (catalogId: string) =>
    adsRequest.get(`${ADS_API}/catalogs/${catalogId}/product-sets`),
  getCatalogProducts: (catalogId: string) =>
    adsRequest.get(`${ADS_API}/catalogs/${catalogId}/products`),

  getLeadForms: () => adsRequest.get(`${ADS_API}/leads/forms`),
  createLeadForm: (data: any) => adsRequest.post(`${ADS_API}/leads/forms`, data),
  presignLeadFormFile: (data: { fileType: string; fileSize: number }) =>
    adsRequest.post(`${ADS_API}/leads/forms/gated-file/presign`, data),
  getLeadFormLeads: (formId: string, params?: any) =>
    adsRequest.get(`${ADS_API}/leads/forms/${formId}/leads`, { params }),
  getAllPageLeads: (params?: any) => adsRequest.get(`${ADS_API}/leads`, { params }),
  getMetaLeadFilterOptions: (params?: { from?: string; to?: string }) =>
    adsRequest.get(`${ADS_API}/leads/filter-options`, { params }),
  getLeadGenSubscriptionStatus: () => adsRequest.get(`${ADS_API}/leads/subscription-status`),
  resubscribeLeadGenPages: () => adsRequest.post(`${ADS_API}/leads/resubscribe`, {}),

  getAgencies: () => adsRequest.get(`${ADS_API}/setup/agencies`),
  getAdvertisableApplications: () => adsRequest.get(`${ADS_API}/setup/advertisable-applications`),
  getAssignedUsers: () => adsRequest.get(`${ADS_API}/setup/assigned-users`),
  updateAdAccount: (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) =>
    apiClient.patch(`${ADS_API}/setup/account`, data),
  getReachFrequencyPredictions: () => adsRequest.get(`${ADS_API}/setup/reach-frequency-predictions`),
  getConnectedInstagramAccounts: () => adsRequest.get(`${ADS_API}/setup/connected-instagram-accounts`),

  copyAd: (adId: string, data?: { status_option?: string; adset_id?: string }) =>
    adsRequest.post(`${ADS_API}/meta-ads/${adId}/copy`, data ?? {}),
  getAdPreviews: (adId: string, params: { ad_format: string; creative_feature?: string }) =>
    adsRequest.get(`${ADS_API}/meta-ads/${adId}/previews`, { params }),

  createInsightsReport: (data: { object_id: string; level?: string; date_preset?: string; time_range?: any; breakdowns?: string; fields?: string }) =>
    adsRequest.post(`${ADS_API}/insights/report`, data),
  pollInsightsReport: (reportRunId: string) =>
    adsRequest.get(`${ADS_API}/insights/report/${reportRunId}`),

  getBroadTargetingCategories: () => adsRequest.get(`${ADS_API}/targeting/broad-categories`),

  getAdPixels: () => adsRequest.get(`${ADS_API}/pixels`),
  createAdPixel: (data: { name: string; business_account_id?: string | null; connect_existing?: boolean }) =>
    adsRequest.post(`${ADS_API}/pixels`, data),

  getCapiDatasets: () => adsRequest.get(`${ADS_API}/capi/datasets`),
  getCustomConversions: () => adsRequest.get(`${ADS_API}/capi/custom-conversions`),

  getPagePosts: (limit = 25) => adsRequest.get(`${ADS_API}/page-posts`, { params: { limit } }),
  getInstagramMedia: (limit = 25) => adsRequest.get(`${ADS_API}/instagram-media`, { params: { limit } }),

  uploadVideoByUrl: (data: { video_url: string; title?: string; fallback_url?: string }) =>
    adsRequest.post(`${ADS_API}/upload-video-url`, data),
  getVideoStatus: (videoId: string) =>
    adsRequest.get(`${ADS_API}/videos/${encodeURIComponent(videoId)}/status`),
  getVideoMedia: (videoId: string) =>
    adsRequest.get(`${ADS_API}/videos/${encodeURIComponent(videoId)}/media`),
  getAdImageDetails: (hash: string) =>
    adsRequest.get(`${ADS_API}/images/${encodeURIComponent(hash)}/details`),

  generateFromImage: async (file: File, organizationId?: string | null) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-image", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }

    return json;
  },

  generateFromVideo: async (
    input: { file?: File; videoUrl?: string },
    organizationId?: string | null
  ) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }
    if (!input.file && !input.videoUrl) {
      throw new Error("Provide either a video file or a video_url");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    if (input.file) {
      formData.append("video", input.file);
    } else if (input.videoUrl) {
      formData.append("video_url", input.videoUrl);
    }
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-video", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }
    return json;
  },
};

export function useActiveAdCurrency(): string | undefined {
  const setup = useAdsSetupStatus();
  const data = setup.data as any;
  const accounts = data?.accounts as Array<any> | undefined;
  return data?.account?.currency ?? accounts?.find((a) => a?.is_active)?.currency ?? undefined;
}

export function useAdsSetupStatus() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const queryClient = useQueryClient();
  const selectedId = useSyncExternalStore(
    subscribeSelection,
    () => getSelectedAdAccountId(workspaceId ?? null),
    () => null,
  );
  useEffect(() => bindAdAccountSelectionResets(queryClient), [queryClient]);
  const selectForAccount = useCallback(
    <T>(data: T) => statusForSelection(data, selectedId),
    [selectedId],
  );
  const query = useQuery({
    queryKey: ["ads", "setup-status", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getSetupStatus(businessAccountId, workspaceId);
      return res.data;
    },
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
    retry: 0,
    select: selectForAccount,
  });
  const statusData = query.data;
  useEffect(() => {
    if (!workspaceId || !statusData || typeof statusData !== "object") return;
    reconcileAdAccountSelection(workspaceId, statusData);
  }, [workspaceId, statusData]);
  return query;
}

export interface AdAccountOverviewRow {
  ad_account_id: string;
  ad_account_name: string | null;
  currency: string | null;
  timezone: string | null;
  is_default: boolean;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  cost_per_lead: number | null;
  last_synced_at: string | null;
  sync_status: string | null;
  token_status: string | null;
}

export interface AdAccountCurrencyTotal {
  currency: string | null;
  spend: number;
  leads: number;
  impressions: number;
  clicks: number;
  cost_per_lead: number | null;
  account_count: number;
}

export interface AdAccountsOverview {
  accounts: AdAccountOverviewRow[];
  totals_by_currency: AdAccountCurrencyTotal[];
  window: { since: string | null; until: string | null };
}

export function useAdsAccountsOverview(
  win: { datePreset?: string; since?: string; until?: string } = {},
  enabled = true,
) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const since = win.since && win.until ? win.since : null;
  const until = win.since && win.until ? win.until : null;
  const datePreset = since ? null : win.datePreset ?? null;
  return useQuery({
    queryKey: ["ads", "overview-accounts", workspaceId, datePreset, since, until],
    queryFn: async (): Promise<AdAccountsOverview> => {
      const res = await adsApi.getAccountsOverview({
        datePreset: datePreset ?? undefined,
        since: since ?? undefined,
        until: until ?? undefined,
      });
      if (res.error || res.success === false || !res.data) {
        throw ApiError.fromResult(res, "Failed to load the ad accounts overview");
      }
      return res.data as AdAccountsOverview;
    },
    enabled: enabled && !!workspaceId,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === workspaceId ? previous : undefined),
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export function useSetDefaultAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (adAccountId: string) => {
      const res = await adsApi.setDefaultAdAccount(bareAdAccountId(adAccountId));
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "overview-accounts"] });
      toast({ variant: "success", title: "Default ad account updated" });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Failed to set the default ad account" } });
    },
  });
}

export function useLeadGenSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: ["ads", "leadgen-subscription-status"],
    queryFn: async () => {
      const res = await adsApi.getLeadGenSubscriptionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  });
}

export function useResubscribeLeadGenPages() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.resubscribeLeadGenPages();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      const subscribed = data?.subscribed?.length ?? 0;
      const failed = data?.failed?.length ?? 0;
      toast({
        variant: failed > 0 ? "warning" : "success",
        title:
          failed > 0
            ? `Subscribed ${subscribed} page(s); ${failed} need reconnecting`
            : `Subscribed ${subscribed} page(s) to lead capture`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to subscribe pages" } });
    },
  });
}

export function useAdsBalance(enabled = true) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const { effectiveId } = useAdAccountSelection();
  const accountKey = bareAdAccountId(effectiveId) || null;
  return useQuery({
    queryKey: ["ads", "balance", workspaceId, accountKey],
    queryFn: async () => {
      const res = await adsApi.getBalance();
      return res.data;
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaigns(
  params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "campaigns", params],
    queryFn: async () => {
      const res = await adsApi.getCampaigns(params);
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaign(id: string | null) {
  return useQuery({
    queryKey: ["ads", "campaign", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaign(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useAdsCampaignInsights(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "insights", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaignInsights(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsLeadsChart(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "leads-chart", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getLeadsChart(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsFlows() {
  return useQuery({
    queryKey: ["ads", "flows"],
    queryFn: async () => {
      const res = await adsApi.getFlows();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdsFetchedFromMeta(
  datePreset: string = "maximum",
  _accountId?: string,
  range?: { since: string; until: string },
  options: { autoLoad?: boolean } = {}
) {
  const rangeKey = range?.since && range?.until ? `${range.since}:${range.until}` : null;
  const setupStatus = useAdsSetupStatus();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const accountKey: string | null = setupStatus.data?.account?.ad_account_id ?? null;
  const connected = !!setupStatus.data?.connected;
  return useQuery({
    queryKey: ["ads", "fetched-from-meta", workspaceId, accountKey, datePreset, rangeKey],
    queryFn: async () => {
      const res = await adsApi.fetchAdsFromMeta(datePreset, range);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: !!options.autoLoad && connected && !!accountKey,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 1),
  });
}

export const BREAKDOWN_DIMENSIONS = [
  { key: "age_gender", label: "Age & Gender" },
  { key: "placement", label: "Placement" },
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "device", label: "Device" },
  { key: "time_of_day", label: "Time of Day" },
] as const;

export type BreakdownDimensionKey = (typeof BREAKDOWN_DIMENSIONS)[number]["key"];

export interface BreakdownRow {
  bucket: string;
  keys: Record<string, string>;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  cost_per_lead: number | null;
}

export interface BreakdownResult {
  dimension: BreakdownDimensionKey;
  label: string;
  rows: BreakdownRow[];
  totals: { spend: number; impressions: number; clicks: number; leads: number };
  date_preset: string;
  currency: string;
  ad_account_id: string;
  source?: string;
  unavailable_fields?: string[];
}

export function useAdsBreakdown(
  dimension: BreakdownDimensionKey,
  datePreset: string = "last_30d",
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "breakdown", dimension, datePreset],
    queryFn: async (): Promise<BreakdownResult> => {
      const res = await adsApi.getBreakdown(dimension, datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load breakdown");
      }
      return res.data as BreakdownResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  pctOfTop: number | null;
  pctOfPrev: number | null;
  dropOffPct: number | null;
  costPer: number | null;
  costPerThousand?: number | null;
}

export interface FunnelResult {
  steps: FunnelStep[];
  spend: number;
  date_preset: string;
  currency: string;
  crm_buckets: { won: number; engaged: number; no_response: number; negative: number; open: number };
  crm_available?: boolean;
  since?: string | null;
  until?: string | null;
}

export function useAdsFunnel(win: string | AdsInsightsWindow = "last_30d", enabled = true) {
  const window = insightsWindowOf(win);
  return useQuery({
    queryKey: ["ads", "funnel", window.datePreset ?? null, window.since ?? null, window.until ?? null],
    queryFn: async (): Promise<FunnelResult> => {
      const res = await adsApi.getFunnel(window);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load funnel");
      }
      return res.data as FunnelResult;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export const REPORT_METRICS: Array<{ key: string; label: string; kind: string }> = [
  { key: "spend", label: "Spend", kind: "currency" },
  { key: "impressions", label: "Impressions", kind: "number" },
  { key: "reach", label: "Reach", kind: "number" },
  { key: "clicks", label: "Clicks", kind: "number" },
  { key: "leads", label: "Leads", kind: "number" },
  { key: "ctr", label: "CTR", kind: "percent" },
  { key: "cpc", label: "CPC", kind: "currency" },
  { key: "cpm", label: "CPM", kind: "currency" },
  { key: "frequency", label: "Frequency", kind: "number2" },
  { key: "cost_per_lead", label: "Cost / lead", kind: "currency" },
];
export const REPORT_LEVELS: Array<{ key: string; label: string }> = [
  { key: "campaign", label: "Campaigns" },
  { key: "adset", label: "Ad Sets" },
  { key: "ad", label: "Ads" },
];
export const DEFAULT_REPORT_METRICS = ["spend", "impressions", "clicks", "ctr", "leads", "cost_per_lead"];

export interface ReportRequest {
  level: string;
  metrics: string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  date_preset?: string;
}
export interface ReportColumn { key: string; label: string; kind: string }
export interface ReportRow {
  id: string | null;
  name: string;
  status: string | null;
  values: Record<string, number | null>;
}
export interface ReportResult {
  level: string;
  metrics: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: Record<string, number | null>;
  date_preset: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}

export function useAdsCustomReport(req: ReportRequest, enabled: boolean) {
  return useQuery({
    queryKey: ["ads", "custom-report", req],
    queryFn: async (): Promise<ReportResult> => {
      const res = await adsApi.buildCustomReport(req);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to build report");
      return res.data as ReportResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface WeeklyDigest {
  orgName: string;
  periodLabel: string;
  currency: string;
  current: { spend: number; leads: number; cost_per_lead: number };
  deltas: { spend: number | null; leads: number | null; cost_per_lead: number | null };
  topCreatives: Array<{ name: string; kind: string; spend: number; leads: number; cost_per_lead: number | null }>;
  funnel: Array<{ label: string; count: number; pctOfPrev: number | null }>;
  anomaly: { reason: string } | null;
  revenue: { revenue: number; revenuePerSpend: number | null; netProfit: number } | null;
}
export interface WeeklyDigestResult {
  digest: WeeklyDigest;
  html: string;
  ad_account_id?: string;
  unavailable?: string[];
  complete?: boolean;
  emails_enabled?: boolean;
}

export function useAdsWeeklyDigest(enabled = true) {
  return useQuery({
    queryKey: ["ads", "weekly-digest"],
    queryFn: async (): Promise<WeeklyDigestResult> => {
      const res = await adsApi.getWeeklyDigest();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load weekly digest");
      return res.data as WeeklyDigestResult;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface AnomalyResult {
  anomaly: boolean;
  kind: "cpl_spike" | "spend_spike" | null;
  latest: { date: string; spend: number; leads: number; cost_per_lead: number } | null;
  baselineCpl: number | null;
  baselineSpend: number | null;
  cplRatio: number | null;
  spendRatio: number | null;
  reason: string | null;
  ad_account_id: string;
  currency: string;
  history_days: number;
  latest_snapshot_date?: string | null;
  stale?: boolean;
}

export interface AdsSyncStatus {
  connected: boolean;
  status: string;
  ad_account_id?: string | null;
  is_running?: boolean;
  is_queued?: boolean;
  has_data?: boolean;
  last_synced_at?: string | null;
  synced_through?: string | null;
  last_run_truncated?: boolean;
  account_timezone?: string | null;
  last_error_type?: string | null;
  last_error_message?: string | null;
  last_started_at?: string | null;
  breakdowns_synced_through?: string | null;
  import_complete?: boolean;
  breakdowns_complete?: boolean;
  retry_available_at?: string | null;
}

export interface AdsSyncResult {
  status: string;
  ad_account_id?: string | null;
  partial?: boolean;
  insights_complete?: boolean;
  structure_skipped?: boolean;
  chunks_done?: number;
  chunks_total?: number;
  archived?: number;
  truncated?: boolean;
}

export { sameAdAccount, isPinnedAccountQuery, type AdsSyncPin };

export function useAdsSyncStatus(enabled = true) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const selected = useSelectedAdAccountId();
  return useQuery({
    queryKey: ["ads", "sync-status", workspaceId, selected ? bareAdAccountId(selected) : null],
    queryFn: async (): Promise<AdsSyncStatus> => {
      const res = await adsApi.getSyncStatus();
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as AdsSyncStatus;
    },
    enabled,
    staleTime: 10 * 1000,
    refetchInterval: (query) => (query.state.data?.is_running || query.state.data?.is_queued ? 3000 : false),
  });
}

function syncRoundFor(queryClient: ReturnType<typeof useQueryClient>) {
  return {
    syncOnce: async (pin: AdsSyncPin): Promise<SyncRoundResult> => {
      const res = await adsApi.syncNow(pin);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as SyncRoundResult;
    },
    readStatus: (pin: AdsSyncPin): Promise<AdsSyncStatus> =>
      queryClient.fetchQuery({
        queryKey: ["ads", "sync-status", pin.workspaceId, bareAdAccountId(pin.adAccountId)],
        queryFn: async (): Promise<AdsSyncStatus> => {
          const res = await adsApi.getSyncStatus(pin);
          if (res.error || res.success === false) throw ApiError.fromResult(res);
          return res.data as AdsSyncStatus;
        },
        staleTime: 3000,
      }),
    onRound: (pin: AdsSyncPin) => {
      queryClient.invalidateQueries({
        queryKey: ["ads", "fetched-from-meta"],
        predicate: (query) => isPinnedAccountQuery(query.queryKey, pin),
      }, { cancelRefetch: false });
      queryClient.invalidateQueries({ queryKey: ["ads", "all-accounts", "fetched", pin.workspaceId] }, { cancelRefetch: false });
      queryClient.invalidateQueries({ queryKey: ["ads", "sync-status"] });
    },
  };
}

type ToastFn = ReturnType<typeof useToast>["toast"];

function toastSyncError(error: any, toast: ToastFn, mode: "manual" | "import" = "manual", onSyncAgain?: () => void) {
  const code = errorCodeOf(error);
  if (code === "RATE_LIMIT" || code === "META_RATE_LIMITED" || error?.statusCode === 429 || isMetaRateLimitError(error)) {
    toast({
      variant: "destructive",
      title: "Meta is rate limiting us",
      description:
        mode === "import"
          ? "Meta asked us to slow down. Everything imported so far is saved, and the import will continue later."
          : "Meta asked us to slow down for this ad account. The Sync button shows when you can sync again.",
    });
    return;
  }
  if (code === "AUTH" || error?.statusCode === 401) {
    toast({
      variant: "destructive",
      title: "Reconnect Facebook",
      description: "Your Facebook sign-in has expired. Please reconnect Facebook to sync.",
    });
    return;
  }
  if (code === "NO_WORKSPACE") {
    toast({
      variant: "destructive",
      title: "This ad account can't be synced",
      description: "It isn't linked to a workspace. Reconnect it from Ads settings.",
    });
    return;
  }
  if (code === "TIMEOUT") {
    toast({
      variant: "destructive",
      title: "Sync timed out",
      description:
        mode === "import"
          ? "Your Meta Ads data took too long to load. Everything imported so far is saved."
          : "Your Meta Ads data took too long to load. Please press Sync again.",
      ...(onSyncAgain && {
        action: createElement(
          ToastAction,
          { altText: "Sync again", className: "ml-12 sm:ml-0", onClick: onSyncAgain },
          "Sync again",
        ) as unknown as ToastActionElement,
      }),
    });
    return;
  }
  showApiError(error, { fallback: { title: mode === "import" ? "Import from Meta stopped" : "Sync failed" } });
}

export function useStartFirstImport() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useCallback(
    async (pins: AdsSyncPin[]): Promise<PromiseSettledResult<SyncRunResult>[]> => {
      const seen = new Set<string>();
      const unique = pins.filter((pin) => {
        if (!pin?.workspaceId || !pin?.adAccountId) return false;
        const key = pinKey(pin);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (!unique.length) return [];
      const label = (pin: AdsSyncPin) => pin.adAccountName?.trim() || "your ad account";
      toast({
        title: unique.length === 1 ? `Importing ${label(unique[0])} from Meta…` : `Importing ${unique.length} ad accounts from Meta…`,
        description: "Campaigns, ad sets, ads and their numbers load in the background.",
      });
      const round = syncRoundFor(queryClient);
      const results = await Promise.allSettled(unique.map((pin) => runPinnedSync(pin, { mode: "import", ...round })));
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed) {
        toastSyncError(failed.reason, toast, "import");
        return results;
      }
      const stillRunning = results.filter((r) => r.status === "fulfilled" && r.value.status === "running").length;
      const unfinished = results.filter((r) => r.status === "fulfilled" && !r.value.complete && r.value.status !== "running").length;
      if (stillRunning) {
        toast({
          title: "Still importing in the background",
          description: "Large ad accounts take a while. Everything keeps loading, and the numbers fill in as it does.",
        });
        return results;
      }
      if (unfinished) {
        toast({
          title: "Import paused",
          description: "What's imported so far is saved. It continues on your next visit or with the next scheduled sync.",
        });
        return results;
      }
      toast({
        variant: "success",
        title: unique.length === 1 ? `Imported ${label(unique[0])} from Meta` : `Imported ${unique.length} ad accounts from Meta`,
      });
      return results;
    },
    [queryClient, toast],
  );
}

export function useAdsSyncNow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const syncAgainRef = useRef<(pin: AdsSyncPin) => void>(() => {});

  const mutation = useMutation({
    mutationFn: (pin: AdsSyncPin): Promise<SyncRunResult> =>
      runPinnedSync(pin, {
        mode: "manual",
        ...syncRoundFor(queryClient),
        onQueued: () => {
          const name = pin.adAccountName?.trim() || null;
          toast({
            title: name ? `Syncing ${name} from Meta…` : "Syncing from Meta…",
            description: "This runs in the background. You can keep working; the numbers update when it finishes.",
          });
        },
      }),
    onSuccess: (result, pin) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      const name = pin.adAccountName?.trim() || null;
      if (result.status === "running") {
        toast({
          title: "Still syncing in the background",
          description: `${name ? `${name}: ` : ""}the numbers update when it finishes.`,
        });
        return;
      }
      if (result.partial) {
        toast({
          title: name ? `${name}: sync partially complete` : "Sync partially complete",
          description: `Synced ${result.chunks_done ?? 0} of ${result.chunks_total ?? 0} periods. Run it again to continue.`,
        });
        return;
      }
      toast({ variant: "success", title: name ? `Synced ${name} from Meta` : "Synced from Meta" });
    },
    onError: (error: any, pin) => {
      toastSyncError(error, toast, "manual", () => syncAgainRef.current(pin));
    },
  });

  syncAgainRef.current = (pin) => mutation.mutate(pin);
  return mutation;
}

export function useAdsAnomalies(enabled = true) {
  return useQuery({
    queryKey: ["ads", "anomalies"],
    queryFn: async (): Promise<AnomalyResult> => {
      const res = await adsApi.getAnomalies();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load anomalies");
      return res.data as AnomalyResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface CreativeRow {
  creative_key: string;
  creative_id: string | null;
  name: string;
  kind: "video" | "image" | "unknown";
  thumbnail: string | null;
  ad_count: number;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_lead: number | null;
}
export interface VideoRow {
  ad_id: string | null;
  name: string;
  thumbnail: string | null;
  spend: number;
  plays: number;
  thruplays: number;
  p25: number; p50: number; p75: number; p100: number;
  rate25: number | null; rate50: number | null; rate75: number | null; rate100: number | null;
  thruplayRate: number | null;
}
export interface CreativeInsightsResult {
  creatives: CreativeRow[];
  videos: VideoRow[];
  date_preset: string;
  currency: string;
}

export function useAdsCreativeInsights(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "creative-insights", datePreset],
    queryFn: async (): Promise<CreativeInsightsResult> => {
      const res = await adsApi.getCreativeInsights(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load creative insights");
      return res.data as CreativeInsightsResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface PacingAdSet {
  id: string | null;
  name: string | null;
  dailyBudget: number;
  spentToday: number;
  pctOfBudget: number | null;
  alert: "above_daily" | "on_track" | "underspend" | "no_budget";
}
export interface PacingResult {
  adsets: PacingAdSet[];
  counts: { above_daily: number; on_track: number; underspend: number; no_budget: number };
  projection: {
    spendThisMonth: number;
    dayOfMonth: number;
    daysInMonth: number;
    avgPerDay: number;
    projectedMonthEnd: number;
  };
  currency: string;
  time_zone?: string | null;
}

export function useAdsSpendPacing(enabled = true) {
  return useQuery({
    queryKey: ["ads", "pacing"],
    queryFn: async (): Promise<PacingResult> => {
      const res = await adsApi.getSpendPacing();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load pacing");
      return res.data as PacingResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface RevenuePerLeadResult {
  leads: number;
  sales: number;
  revenue: number | null;
  spend: number;
  revenuePerLead: number | null;
  revenuePerSale: number | null;
  revenuePerSpend: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
  netProfit: number | null;
  profitable: boolean | null;
  date_preset: string;
  currency: string;
  revenueCurrency?: string | null;
  revenueByCurrency?: Array<{ currency: string; amount: number }>;
  revenueCurrencyAssumed?: boolean;
  comparable?: boolean;
  crm_available?: boolean;
  crm_error?: string | null;
  since?: string | null;
  until?: string | null;
}

export function useAdsRevenuePerLead(win: string | AdsInsightsWindow = "last_30d", enabled = true) {
  const window = insightsWindowOf(win);
  return useQuery({
    queryKey: ["ads", "revenue-per-lead", window.datePreset ?? null, window.since ?? null, window.until ?? null],
    queryFn: async (): Promise<RevenuePerLeadResult> => {
      const res = await adsApi.getRevenuePerLead(window);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load revenue per lead");
      return res.data as RevenuePerLeadResult;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface MultiAccountSummary {
  accounts: Array<{
    ad_account_id: string;
    ad_account_name: string;
    currency: string;
    spend: number;
    leads: number;
    cost_per_lead: number;
    error: string | null;
  }>;
  totals: {
    account_count: number;
    date_preset: string;
    total_spend: number;
    total_leads: number;
    cost_per_lead: number;
    currency: string;
  };
}

export function useMultiAccountSummary() {
  return useMutation({
    mutationFn: async (
      vars: { adAccountIds: string[]; datePreset?: string; since?: string; until?: string }
    ): Promise<MultiAccountSummary> => {
      const res = await adsApi.getMultiAccountSummary(vars.adAccountIds, {
        datePreset: vars.datePreset,
        since: vars.since,
        until: vars.until,
      });
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load multi-account summary");
      }
      return res.data as MultiAccountSummary;
    },
  });
}

export function useProductCatalogs() {
  return useQuery({
    queryKey: ["ads", "catalogs"],
    queryFn: async () => {
      const res = await adsApi.getProductCatalogs();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProductSets(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-product-sets", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProductSets(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProducts(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-products", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProducts(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProductCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { business_id: string; name: string; vertical?: string }) => {
      const res = await adsApi.createProductCatalog(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalogs"] });
      toast({ variant: "success", title: "Catalog created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create catalog" } });
    },
  });
}

export function useAddProductToCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ catalogId, product }: { catalogId: string; product: any }) => {
      const res = await adsApi.addProductToCatalog(catalogId, product);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalog-products", variables.catalogId] });
      toast({ variant: "success", title: "Product added to catalog" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to add product" } });
    },
  });
}

export function useGenerateMetaAdsFromImage() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, organizationId }: { file: File; organizationId?: string | null }) => {
      return adsApi.generateFromImage(file, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

export function useGenerateMetaAdsFromVideo() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      videoUrl,
      organizationId,
    }: {
      file?: File;
      videoUrl?: string;
      organizationId?: string | null;
    }) => {
      return adsApi.generateFromVideo({ file, videoUrl }, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

function recoverMissedLeadsIfRecommended(
  data: unknown,
  variables: unknown,
  toast: ReturnType<typeof useToast>["toast"],
  queryClient: ReturnType<typeof useQueryClient>
) {
  const result = data as { lead_recovery_recommended?: boolean; ad_account_id?: string } | null;
  if (!result?.lead_recovery_recommended) return;
  const adAccountId = bareAdAccountId(
    (variables as { ad_account_id?: string } | null)?.ad_account_id ?? result.ad_account_id
  );
  if (!adAccountId) return;
  void adsApi
    .syncLeads({ ad_account_id: adAccountId })
    .then((res) => {
      if (res.error) throw ApiError.fromResult(res);
      queryClient.invalidateQueries({ queryKey: ["ads", "all-page-leads"] });
      toast(leadRecoverySummary(res.data));
    })
    .catch(() => {
      toast({
        variant: "warning",
        title: "Couldn't recover missed leads",
        description:
          "The ad account is connected. Use Sync on the Leads tab to pull in leads from while it was disconnected.",
      });
    });
}

export function useAdsSetupConnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const startFirstImports = useStartFirstImport();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.connectAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown, variables: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "balance"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "connected-instagram"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "overview-accounts"] });
      toast({ variant: "success", title: "Ad account connected successfully" });
      recoverMissedLeadsIfRecommended(data, variables, toast, queryClient);
      const row = (data ?? null) as { ad_account_id?: string | null; ad_account_name?: string | null; workspace_id?: string | null } | null;
      const pin = syncPinFor(row?.workspace_id || workspaceId || adsCurrentWorkspaceId(), row);
      if (pin) void startFirstImports([pin]);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to connect ad account" } });
    },
  });
}

export function useAvailableAdAccounts(
  enabled = false,
  opts?: { includeBusinessScoped?: boolean }
) {
  const includeBusinessScoped = !!opts?.includeBusinessScoped;
  return useQuery({
    queryKey: ["ads", "available-ad-accounts", { includeBusinessScoped }],
    queryFn: async () => {
      const res = await adsApi.getAvailableAdAccounts({ includeBusinessScoped });
      if (res.error) throw ApiError.fromResult(res, "Failed to load ad accounts from Facebook");
      return res.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: unknown) => shouldRetryMetaQuery(failureCount, error, 2),
  });
}

export function useSwitchAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const startFirstImports = useStartFirstImport();

  return useMutation({
    mutationFn: async ({ switchKind: _switchKind, ...data }: any) => {
      const res = await adsApi.switchAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown, variables: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      queryClient.invalidateQueries({ queryKey: ["instagram-ads"] });
      const vars = (variables ?? {}) as { switchKind?: string; page_name?: string | null };
      toast(
        vars.switchKind === "page"
          ? {
              variant: "success",
              title: "Page switched",
              description: vars.page_name
                ? `${vars.page_name} is now used for CTWA ads and lead forms.`
                : "The new page is now used for CTWA ads and lead forms.",
            }
          : { variant: "success", title: "Ad account switched successfully" },
      );
      recoverMissedLeadsIfRecommended(data, variables, toast, queryClient);
      const row = (data ?? null) as { ad_account_id?: string | null; ad_account_name?: string | null; workspace_id?: string | null } | null;
      const pin = syncPinFor(row?.workspace_id || workspaceId || adsCurrentWorkspaceId(), row);
      if (pin) void importIfUnfinished(pin, startFirstImports);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to switch ad account" } });
    },
  });
}

export async function importIfUnfinished(
  pin: AdsSyncPin,
  startFirstImports: (pins: AdsSyncPin[]) => Promise<unknown>,
): Promise<boolean> {
  try {
    const res = await adsApi.getSyncStatus(pin);
    if (res.error || res.success === false || !res.data) return false;
    if (!shouldAutoImport(res.data as AdsSyncStatus)) return false;
  } catch {
    return false;
  }
  void startFirstImports([pin]);
  return true;
}

export function useAdsDisconnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? adsCurrentWorkspaceId();

  return useMutation({
    mutationFn: async (
      target?: string | { adAccountId?: string; pauseCampaigns?: boolean; nowUsingIfCleared?: string | null }
    ) => {
      const adAccountId = typeof target === "string" ? target : target?.adAccountId;
      const pauseCampaigns =
        typeof target === "object" && target ? target.pauseCampaigns : undefined;
      const res = await adsApi.disconnect(adAccountId, businessAccountId, pauseCampaigns);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown, target) => {
      const result = data as DisconnectResult | null;
      const targetId = typeof target === "string" ? target : target?.adAccountId;
      const removedId = bareAdAccountId(result?.disconnected_ad_account_id ?? targetId);
      const cleared =
        !!removedId &&
        clearAdAccountSelection(workspaceId, { onlyIf: removedId });
      if (!cleared) queryClient.invalidateQueries({ queryKey: ["ads"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "overview-accounts"] });
      const nowUsing = cleared && typeof target === "object" && target ? target.nowUsingIfCleared ?? null : null;
      toast(summarizeDisconnectResult(result, { nowUsing }));
    },
    onError: (error, target) => {
      const pauseCampaigns =
        typeof target === "object" && target ? target.pauseCampaigns : undefined;
      if (disconnectBlockedMessage(error, pauseCampaigns)) return;
      showApiError(error, { fallback: { title: "Failed to disconnect" } });
    },
  });
}

export function useAdsDisconnectPreview(adAccountId: string | null | undefined, open: boolean) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const id = bareAdAccountId(adAccountId);
  return useQuery({
    queryKey: ["ads", "disconnect-preview", workspaceId, id],
    queryFn: async () => {
      const res = await adsApi.getDisconnectPreview(id);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as DisconnectPreview;
    },
    enabled: open && !!id && !!workspaceId,
    staleTime: 0,
    gcTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

export interface CampaignDraftSummary {
  id: string;
  name: string;
  campaign_type: string;
  ad_account_id: string;
  matches_active_account: boolean;
  last_error: string | null;
  saved_at: string | null;
  draft_kind?: "campaign" | "adset" | "ad";
  target?: CampaignDraftTarget | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignDraftTarget {
  campaignId?: string;
  campaignName?: string;
  campaignBudgetOptimized?: boolean;
  campaignLifetimeBudget?: boolean;
  adSetId?: string;
  adSetName?: string;
  destination?: string;
}

export interface CampaignDraft extends CampaignDraftSummary {
  wizard: Record<string, unknown>;
  payload: Record<string, unknown> | null;
}

export function useCampaignDrafts(enabled = true) {
  const workspaceId = adsCurrentWorkspaceId();
  return useQuery({
    queryKey: ["ads", "campaign-drafts", workspaceId],
    queryFn: async () => {
      const res: any = await adsApi.listCampaignDrafts();
      if (res.error) throw ApiError.fromResult(res);
      return (Array.isArray(res.data) ? res.data : []) as CampaignDraftSummary[];
    },
    enabled: enabled && !!workspaceId,
    staleTime: 30_000,
  });
}

export function useCampaignDraft(draftId: string | null) {
  return useQuery({
    queryKey: ["ads", "campaign-draft", draftId],
    queryFn: async () => {
      const res: any = await adsApi.getCampaignDraft(draftId as string);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as CampaignDraft;
    },
    enabled: !!draftId,
    retry: false,
    staleTime: Infinity,
  });
}

export function useSaveCampaignDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      draft_id?: string;
      name?: string;
      wizard: Record<string, unknown>;
      payload?: Record<string, unknown>;
      last_error?: string;
      draft_kind?: "campaign" | "adset" | "ad";
      target?: CampaignDraftTarget;
    }) => {
      const res: any = await adsApi.saveCampaignDraft(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as CampaignDraftSummary;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign-drafts"] });
      if (saved?.id) queryClient.invalidateQueries({ queryKey: ["ads", "campaign-draft", saved.id], refetchType: "none" });
    },
  });
}

export function useDiscardCampaignDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draftId: string) => {
      const res: any = await adsApi.deleteCampaignDraft(draftId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSettled: (_data, _error, draftId) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign-drafts"] });
      queryClient.removeQueries({ queryKey: ["ads", "campaign-draft", draftId] });
    },
  });
}

export function useDeleteCampaignDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draftId: string) => {
      const res: any = await adsApi.deleteCampaignDraft(draftId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign-drafts"] });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Could not delete the draft" } });
    },
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createCampaign(data);
      if (res.error) throw ApiError.fromResult(res);
      return { data: res.data, warning: res.warning };
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign-drafts"] });
      if (result?.warning) {
        toast({ title: "Campaign created, but paused", description: result.warning, variant: "default" });
      } else {
        toast({ variant: "success", title: "Campaign created successfully" });
      }
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create campaign" } });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res: any = await adsApi.updateCampaign(id, data);
      if (res?.error || res?.success === false) throw ApiError.fromResult(res);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign"] });
      toast({ variant: "success", title: "Campaign updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res: any = await adsApi.deleteCampaign(id);
      if (res?.error || res?.success === false) throw ApiError.fromResult(res);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign deleted" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to delete campaign" } });
    },
  });
}

export function useSyncCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res: any = await adsApi.syncCampaign(id);
      if (res?.error || res?.success === false) throw ApiError.fromResult(res);
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      toast({ variant: "success", title: "Campaign synced from Meta" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Sync failed" } });
    },
  });
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adsApi.duplicateCampaign(id);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign duplicated" });
    },
    onError: (err) => showApiError(err, { fallback: { title: "Could not duplicate the campaign" } }),
  });
}

export function useSearchInterests() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchInterests(query),
  });
}

export function useSearchLocations() {
  return useMutation({
    mutationFn: async (vars: string | { query: string; mode?: string }): Promise<any[]> => {
      const { query, mode } = typeof vars === "string" ? { query: vars, mode: undefined } : vars;
      const res: any = await adsApi.searchLocations(query, mode);
      if (res?.error) throw ApiError.fromResult(res);
      const body = res?.data;
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.data)) return body.data;
      return [];
    },
  });
}

export function useCplEstimate(params: {
  objective?: string;
  dailyBudget?: number;
  enabled?: boolean;
}) {
  const { objective, dailyBudget, enabled = true } = params;
  return useQuery({
    queryKey: ["ads", "cpl-estimate", objective, dailyBudget],
    queryFn: async () => {
      const res = await adsApi.getCplEstimate({ objective, daily_budget: dailyBudget });
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        available: boolean; reason?: string; low?: number; high?: number; mid?: number;
        dailyLeadsLow?: number; dailyLeadsHigh?: number; currency?: string;
        basis?: { historicalCpl?: number; historicalLeads?: number };
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagram(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagram();
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        connected: boolean;
        reason?: string;
        page_id?: string;
        page_name?: string | null;
        instagram?: {
          id: string;
          username: string | null;
          profile_picture_url: string | null;
          followers_count: number | null;
        };
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlatformBreakdown(
  win: { datePreset?: string; since?: string; until?: string } | string | undefined,
  enabled = true,
) {
  const window: { datePreset?: string; since?: string; until?: string } =
    win === undefined ? {} : typeof win === "string" ? { datePreset: win } : win;
  return useQuery({
    queryKey: ["ads", "platform-breakdown", window.datePreset, window.since, window.until],
    queryFn: async () => {
      const res = await adsApi.getPlatformBreakdown(window);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        platforms: Array<{
          platform: string; label: string; spend: number; impressions: number;
          reach: number; clicks: number; leads: number; ctr: number; cpc: number;
          cpm: number; cpl: number | null; spendShare: number;
        }>;
        totals: { spend: number; impressions: number; clicks: number; leads: number; ctr: number; cpc: number; cpl: number | null };
        currency?: string;
      };
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useAccountReach(win: { datePreset?: string; since?: string; until?: string }, enabled = true) {
  return useQuery({
    queryKey: ["ads", "account-reach", win.datePreset, win.since, win.until],
    queryFn: async () => {
      const res = await adsApi.getAccountReach(win);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load reach");
      return res.data as { reach: number | null; no_delivery?: boolean; date_preset?: string | null; since?: string; until?: string };
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export type AllAccountsSection = "fetched" | "reach" | "platforms" | "pacing" | "revenue" | "funnel" | "digest" | "anomalies";

const ALL_ACCOUNTS_SECTIONS: Record<AllAccountsSection, { path: string; windowed: boolean; staleTime: number }> = {
  fetched: { path: "fetch-from-meta", windowed: true, staleTime: 5 * 60 * 1000 },
  reach: { path: "insights/reach", windowed: true, staleTime: 10 * 60 * 1000 },
  platforms: { path: "insights/platforms", windowed: true, staleTime: 10 * 60 * 1000 },
  pacing: { path: "insights/pacing", windowed: false, staleTime: 5 * 60 * 1000 },
  revenue: { path: "insights/revenue-per-lead", windowed: true, staleTime: 10 * 60 * 1000 },
  funnel: { path: "insights/funnel", windowed: true, staleTime: 10 * 60 * 1000 },
  digest: { path: "insights/weekly-digest", windowed: false, staleTime: 15 * 60 * 1000 },
  anomalies: { path: "insights/anomalies", windowed: false, staleTime: 10 * 60 * 1000 },
};

const ALL_ACCOUNTS_CONCURRENCY = 6;
let allAccountsActive = 0;
const allAccountsWaiting: Array<() => void> = [];

async function withAllAccountsSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (allAccountsActive < ALL_ACCOUNTS_CONCURRENCY) allAccountsActive += 1;
  else await new Promise<void>((resolve) => allAccountsWaiting.push(resolve));
  try {
    return await fn();
  } finally {
    const next = allAccountsWaiting.shift();
    if (next) next();
    else allAccountsActive -= 1;
  }
}

function allAccountsParams(section: AllAccountsSection, win: AdsInsightsWindow): Record<string, string> {
  if (!ALL_ACCOUNTS_SECTIONS[section].windowed) return {};
  if (section === "fetched") {
    const params: Record<string, string> = {};
    if (win.datePreset) params.date_preset = win.datePreset;
    if (win.since && win.until) {
      params.since = win.since;
      params.until = win.until;
    }
    return params;
  }
  return insightsWindowParams(win) ?? {};
}

export function useAllAccountsSection<T>(
  section: AllAccountsSection,
  accounts: OverviewAccountRef[],
  win: AdsInsightsWindow,
  enabled = true,
) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const config = ALL_ACCOUNTS_SECTIONS[section];
  const params = allAccountsParams(section, win);
  const accountKey = accounts.map((a) => a.adAccountId).join(",");
  return useQuery({
    queryKey: ["ads", "all-accounts", section, workspaceId, accountKey, params.date_preset ?? null, params.since ?? null, params.until ?? null],
    queryFn: async (): Promise<AccountResult<T>[]> =>
      Promise.all(
        accounts.map((account) =>
          withAllAccountsSlot(async (): Promise<AccountResult<T>> => {
            try {
              const res = await adsRequest.get(`${ADS_API}/${config.path}`, {
                params,
                headers: { [AD_ACCOUNT_HEADER]: account.adAccountId },
              });
              if (res.error || res.success === false || res.data === null || res.data === undefined) {
                return { account, data: null, error: ApiError.fromResult(res) };
              }
              return { account, data: res.data as T, error: null };
            } catch (error) {
              return { account, data: null, error };
            }
          }),
        ),
      ),
    enabled: enabled && !!workspaceId && accounts.length > 0,
    placeholderData: keepPreviousData,
    staleTime: config.staleTime,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useSearchBehaviors() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchBehaviors(query),
  });
}

export function useSearchJobTitles() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchJobTitles(query),
  });
}

export function useSearchAllTargeting() {
  return useMutation({
    mutationFn: async (query: string) => {
      const res = await adsApi.searchAllTargeting(query);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
        failedClasses: string[];
      };
    },
  });
}

export function useTargetingSearch(query: string) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const q = query.trim();
  return useQuery({
    queryKey: ["ads", "targeting-search", workspaceId, q.toLowerCase()],
    queryFn: async () => {
      const res = await adsApi.searchAllTargeting(q);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
        failedClasses: string[];
      };
    },
    enabled: q.length >= 2,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 0,
    placeholderData: keepPreviousData,
  });
}

export function useBrowseTargeting(cls: string | null) {
  return useQuery({
    queryKey: ["ads", "targeting-browse", cls],
    queryFn: async () => {
      if (!cls) return null;
      const res = await adsApi.browseTargeting(cls);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        class: string;
        data: Array<{ id: string; name: string; type: string; audience_size?: number | null; path?: string[] }>;
      };
    },
    enabled: !!cls,
    staleTime: 30 * 60 * 1000,
  });
}

export function useAudienceEstimate() {
  return useMutation({
    mutationFn: async (vars: { targetingSpec: unknown; optimizationGoal?: string }) => {
      const res = await adsApi.estimateAudience(vars.targetingSpec, vars.optimizationGoal);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { available: boolean; reason?: string; lower?: number; upper?: number };
    },
  });
}

export function useOAuthCallback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await adsApi.handleCallback(code);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Could not finish connecting Facebook");
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
    },
  });
}

export function useGetOAuthUrl() {
  return useMutation({
    mutationFn: (purpose: MetaAdsOAuthPurpose | void) => adsApi.getOAuthUrl(purpose || undefined),
  });
}

export function useAdAccountsFromLogin() {
  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }): Promise<LoginAdAccountsResult> => {
      const res = await adsApi.listAdAccountsFromLogin(code, state);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Could not read your ad accounts from Facebook");
      }
      return res.data as LoginAdAccountsResult;
    },
  });
}

export function useConnectAdAccountsFromLogin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ login, adAccountIds }: { login: string; adAccountIds: string[] }): Promise<BusinessLoginConnectResult> => {
      const res = await adsApi.connectFromLogin(login, adAccountIds);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Could not connect the ad accounts you chose");
      }
      return res.data as BusinessLoginConnectResult;
    },
    onSuccess: (data) => {
      if (!data?.results?.some((r) => r.outcome === "connected" || r.outcome === "reconnected")) return;
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "overview-accounts"] });
      for (const r of data.results) {
        if (r.lead_recovery_recommended) {
          recoverMissedLeadsIfRecommended(r, { ad_account_id: r.ad_account_id }, toast, queryClient);
        }
      }
    },
  });
}

export function useUploadAdImage() {
  return useMutation({
    mutationFn: async (imageUrl: string) => {
      const res = await adsApi.uploadImage(imageUrl);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUpdateMetaAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaAdId,
      data,
    }: {
      metaAdId: string;
      data: {
        status?: string;
        name?: string;
        adset_id?: string;
        creative?: { creative_id?: string } & AdCreativeChanges;
      };
    }) => {
      const res = await adsApi.updateMetaAd(metaAdId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-ad-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "edit-context"] });
      toast({
        variant: "success",
        title: variables.data?.creative ? "Creative replaced" : "Ad updated successfully",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad" } });
    },
  });
}

export function useUpdateMetaCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaCampaignId,
      ...fields
    }: { metaCampaignId: string } & CampaignEditPayload) => {
      const res = await adsApi.updateMetaCampaign(metaCampaignId, fields);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-campaign-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "edit-context"] });
      const { metaCampaignId: _id, status, ...rest } = variables;
      const onlyStatus = status !== undefined && Object.keys(rest).length === 0;
      toast({
        variant: "success",
        title: onlyStatus ? "Campaign status updated" : "Campaign updated",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useUpdateMetaAdSet() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaAdSetId,
      ...fields
    }: { metaAdSetId: string } & AdSetEditPayload) => {
      const res = await adsApi.updateMetaAdSet(metaAdSetId, fields);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-campaign-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "edit-context"] });
      const { metaAdSetId: _id, status, ...rest } = variables;
      const onlyStatus = status !== undefined && Object.keys(rest).length === 0;
      toast({ title: onlyStatus ? "Ad Set status updated" : "Ad Set updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad set" } });
    },
  });
}

export function useGenerateAdCopy() {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.generateAdCopy(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useSuppressionStatus() {
  return useQuery({
    queryKey: ["ads", "suppression-status"],
    queryFn: async () => {
      const res = await adsApi.getSuppressionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useEnableSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.enableSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to enable suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Lead suppression enabled",
        description: `Applied to ${data?.updated ?? 0} ad set${data?.updated === 1 ? "" : "s"} (${data?.alreadyExcluded ?? 0} already had it).`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to enable suppression" } });
    },
  });
}

export function useApplySuppressionToActive() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.applySuppressionToActive();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to apply suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Suppression re-applied",
        description: `${data?.updated ?? 0} updated, ${data?.alreadyExcluded ?? 0} already had it, ${data?.failures?.length ?? 0} failed.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to apply suppression" } });
    },
  });
}

export function useBackfillSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.backfillSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to backfill suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Backfill complete",
        description: `Uploaded ${data?.uploaded ?? 0} contacts in ${data?.batches ?? 0} batch${data?.batches === 1 ? "" : "es"}.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Backfill failed" } });
    },
  });
}

export function useUploadAdImageFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadImageFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUploadAdVideoFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadVideoFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUploadAdVideo() {
  return useMutation({
    mutationFn: async ({
      file,
      title,
      onProgress,
      signal,
    }: {
      file: File;
      title?: string;
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
    }) => {
      const { url, fallbackUrl } = await uploadAdVideoToS3(file, { onProgress, signal });

      const res = await adsApi.uploadVideoByUrl({
        video_url: url,
        fallback_url: fallbackUrl,
        title: title || file.name.replace(/\.[^.]+$/, ""),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useMetaAdDetail(metaAdId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-ad-detail", metaAdId],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdDetail(metaAdId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 30 * 1000,
  });
}

export function useMetaCampaignDetail(metaCampaignId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-campaign-detail", metaCampaignId],
    queryFn: async () => {
      if (!metaCampaignId) return null;
      const res = await adsApi.getMetaCampaignDetail(metaCampaignId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaCampaignId,
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export interface AdLocale {
  key: number;
  name: string;
}

export function useAdLocales(enabled = true) {
  return useQuery({
    queryKey: ["ads", "targeting-locales"],
    queryFn: async (): Promise<AdLocale[]> => {
      const res = await adsApi.getAdLocales();
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return Array.isArray(res.data) ? (res.data as AdLocale[]) : [];
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 1),
  });
}

export function useMetaEditContext(level: MetaEditLevel, metaId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ads", "edit-context", level, metaId ?? null],
    queryFn: async (): Promise<MetaEditContext | null> => {
      if (!metaId) return null;
      const res = await adsApi.getMetaEditContext(level, String(metaId));
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as MetaEditContext;
    },
    enabled: enabled && !!metaId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 1),
  });
}

export function useMetaAdSetDetail(metaAdSetId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-adset-detail", metaAdSetId],
    queryFn: async () => {
      if (!metaAdSetId) return null;
      const res = await adsApi.getMetaAdSetDetail(metaAdSetId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdSetId,
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
}

export function useMetaAdInsights(
  metaAdId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-ad-insights", metaAdId, params],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdInsights(metaAdId, params);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 60 * 1000,
  });
}

export function useMetaCampaignInsights(
  metaCampaignId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-campaign-insights", metaCampaignId, params],
    queryFn: async () => {
      if (!metaCampaignId) return null;
      const res = await adsApi.getMetaCampaignInsights(metaCampaignId, params);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        daily: Array<Record<string, any>>;
        totals: Record<string, any> | null;
      };
    },
    enabled: !!metaCampaignId,
    staleTime: 60 * 1000,
  });
}

export function useMetaAdSetInsights(
  metaAdSetId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-adset-insights", metaAdSetId, params],
    queryFn: async () => {
      if (!metaAdSetId) return null;
      const res = await adsApi.getMetaAdSetInsights(metaAdSetId, params);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as {
        daily: Array<Record<string, any>>;
        totals: Record<string, any> | null;
      };
    },
    enabled: !!metaAdSetId,
    staleTime: 60 * 1000,
  });
}

export function useCreateAdSetInCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ metaCampaignId, data }: { metaCampaignId: string; data: any }) => {
      const res = await adsApi.createAdSetInCampaign(metaCampaignId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_result, { metaCampaignId }) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-campaign-detail", metaCampaignId] });
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
    },
  });
}

export function useCreateAdInAdSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ metaAdSetId, data }: { metaAdSetId: string; data: any }) => {
      const res = await adsApi.createAdInAdSet(metaAdSetId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_result, { metaAdSetId }) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail", metaAdSetId] });
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
    },
  });
}

export function useAiAssistant() {
  return useMutation({
    mutationFn: async (data: { question: string; context: any }) => {
      const res = await adsApi.aiAssistant(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useBusinesses(enabled = false) {
  return useQuery({
    queryKey: ["ads", "businesses"],
    queryFn: async () => {
      const res = await adsApi.getBusinesses();
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      toast({ variant: "success", title: "Ad account created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create ad account" } });
    },
  });
}

export function useFundingDetails(enabled = false) {
  const setupStatus = useAdsSetupStatus();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const accountKey = bareAdAccountId(setupStatus.data?.account?.ad_account_id) || null;
  return useQuery({
    queryKey: ["ads", "funding-details", workspaceId, accountKey],
    queryFn: async () => {
      const res = await adsApi.getFundingDetails();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Couldn't load billing details");
      const data = res.data as { ad_account_id?: string } | undefined;
      if (accountKey && data?.ad_account_id && bareAdAccountId(data.ad_account_id) !== accountKey) {
        throw new Error("The active ad account changed while its funding details were loading.");
      }
      return res.data;
    },
    enabled: enabled && !setupStatus.isLoading,
    staleTime: 30 * 1000,
  });
}

export function useLeadForms(enabled = true) {
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  return useQuery({
    queryKey: ["ads", "lead-forms", workspaceId],
    queryFn: async () => {
      const res = await adsApi.getLeadForms();
      return res.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

let lastLeadFormUpload: { workspaceId: string | null; data: string; key: string } | null = null;

function base64Bytes(data: string): Uint8Array {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function resetLeadFormUploadForTests() {
  lastLeadFormUpload = null;
}

export async function prepareLeadFormGatedFile(file: { name?: string; data?: string } | null | undefined) {
  const data = typeof file?.data === "string" ? file.data : "";
  if (!file || !data) return file;
  const workspaceId = adsCurrentWorkspaceId();
  if (lastLeadFormUpload && lastLeadFormUpload.data === data && lastLeadFormUpload.workspaceId === workspaceId) {
    return { name: file.name, key: lastLeadFormUpload.key };
  }
  let bytes: Uint8Array;
  try {
    bytes = base64Bytes(data);
  } catch {
    return file;
  }
  const contentType = gatedFileContentType(bytes);
  if (!contentType) return file;
  const res = await adsApi.presignLeadFormFile({ fileType: contentType, fileSize: bytes.length });
  if (res.error || res.success === false) {
    const err = ApiError.fromResult(res, "The download file could not be uploaded");
    if (err.code === "LEAD_FORM_FILE_STORAGE_UNAVAILABLE") return file;
    throw err;
  }
  const target = (res.data || {}) as { presignedUrl?: string; key?: string };
  if (!target.presignedUrl || !target.key) {
    throw new ApiError({ error: "The download file could not be uploaded. Try again.", message: "The download file could not be uploaded. Try again." });
  }
  await putToS3(target.presignedUrl, new Blob([bytes], { type: contentType }), contentType, {});
  lastLeadFormUpload = { workspaceId, data, key: target.key };
  return { name: file.name, key: target.key };
}

export function useCreateLeadForm() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: any) => {
      const gated = data?.gated_file ? await prepareLeadFormGatedFile(data.gated_file) : undefined;
      let res: Awaited<ReturnType<typeof adsApi.createLeadForm>>;
      try {
        res = await adsApi.createLeadForm(gated ? { ...data, gated_file: gated } : data);
      } catch (err) {
        lastLeadFormUpload = null;
        throw err;
      }
      if (res.error || res.success === false) {
        const err = ApiError.fromResult(res, "Failed to create lead form");
        if (err.code !== "META_REJECTED" && err.code !== "BAD_REQUEST" && err.code !== "EXTERNAL_API_ERROR") lastLeadFormUpload = null;
        throw err;
      }
      lastLeadFormUpload = null;
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "lead-forms"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "instant-forms"] });
      toast({ variant: "success", title: "Lead form created", description: "Your Instant Form has been created on Meta." });
    },
    onError: (err: any) => {
      showApiError(err, { fallback: { title: "Failed to create form" } });
    },
  });
}

export function useLeadFormLeads(formId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["ads", "lead-form-leads", formId, limit],
    queryFn: async () => {
      if (!formId) return null;
      const res = await adsApi.getLeadFormLeads(formId, { limit });
      return res.data;
    },
    enabled: !!formId,
    staleTime: 60 * 1000,
  });
}

/*
  One page of Meta leads for the Leads tab.

  🔴 THE FORM / CAMPAIGN / AD FILTERS GO TO THE SERVER, and therefore into the
  query key. They used to be applied in the browser over the 25 rows already
  fetched, so a campaign that first appears on page 3 looked like it had no
  leads at all. Sending them means `total`, the pager and the CSV export all
  describe the same set the table is showing.
*/
export function useAllPageLeads(
  limit = 100,
  offset = 0,
  bounds: { from?: string; to?: string } = {},
  filters: { q?: string; formId?: string; campaign?: string; ad?: string } = {}
) {
  const from = bounds?.from;
  const to = bounds?.to;
  const q = filters?.q;
  const formId = filters?.formId;
  const campaign = filters?.campaign;
  const ad = filters?.ad;
  const accountKey = useSelectedAdAccountId();
  return useQuery({
    queryKey: [
      "ads",
      "all-page-leads",
      limit,
      offset,
      from ?? null,
      to ?? null,
      accountKey,
      q ?? null,
      formId ?? null,
      campaign ?? null,
      ad ?? null,
    ],
    queryFn: async () => {
      const res = await adsApi.getAllPageLeads({
        limit,
        offset,
        include_options: false,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(q ? { q } : {}),
        ...(formId ? { form_id: formId } : {}),
        ...(campaign ? { campaign } : {}),
        ...(ad ? { ad } : {}),
      });
      return res.data;
    },
    staleTime: 60 * 1000,
    placeholderData: (prev, prevQuery) =>
      prevQuery && prevQuery.queryKey[6] === accountKey ? prev : undefined,
  });
}

export function useMetaLeadFilterOptions(bounds: { from?: string; to?: string } = {}) {
  const from = bounds?.from;
  const to = bounds?.to;
  const accountKey = useSelectedAdAccountId();
  return useQuery({
    queryKey: ["ads", "all-page-leads", "filter-options", from ?? null, to ?? null, accountKey],
    queryFn: async () => {
      const res = await adsApi.getMetaLeadFilterOptions({
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      return res.data as {
        forms?: Array<{ id: string; name: string | null; page_name: string | null }>;
        campaigns?: string[];
        ads?: string[];
        ads_without_name?: boolean;
      } | null;
    },
    staleTime: 60 * 1000,
    placeholderData: (prev, prevQuery) =>
      prevQuery && prevQuery.queryKey[5] === accountKey ? prev : undefined,
  });
}

export function useAdsAudiences(enabled = true) {
  return useQuery({
    queryKey: ["ads", "audiences"],
    queryFn: async () => {
      const res = await adsApi.getAudiences();
      return res.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useAdsAudiencePresets() {
  return useQuery({
    queryKey: ["ads", "audience-presets"],
    queryFn: async () => {
      const res = await adsApi.getAudiencePresets();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useSaveAudiencePreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; targeting_spec: Record<string, unknown>; description?: string }) => {
      const res: any = await adsApi.saveAudiencePreset(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "audience-presets"] });
    },
  });
}

export function useAdAgencies(enabled = false) {
  return useQuery({
    queryKey: ["ads", "agencies"],
    queryFn: async () => {
      const res = await adsApi.getAgencies();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdvertisableApplications(enabled = false) {
  return useQuery({
    queryKey: ["ads", "advertisable-applications"],
    queryFn: async () => {
      const res = await adsApi.getAdvertisableApplications();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignedUsers(enabled = false) {
  return useQuery({
    queryKey: ["ads", "assigned-users"],
    queryFn: async () => {
      const res = await adsApi.getAssignedUsers();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) => {
      const res = await adsApi.updateAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      toast({ variant: "success", title: "Ad account updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad account" } });
    },
  });
}

export function useReachFrequencyPredictions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "reach-frequency-predictions"],
    queryFn: async () => {
      const res = await adsApi.getReachFrequencyPredictions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagramAccounts(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram-accounts"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagramAccounts();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCopyAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ adId, statusOption, adSetId }: { adId: string; statusOption?: string; adSetId?: string }) => {
      const body = {
        ...(statusOption ? { status_option: statusOption } : {}),
        ...(adSetId ? { adset_id: adSetId } : {}),
      };
      const res = await adsApi.copyAd(adId, Object.keys(body).length ? body : undefined);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-adset-detail"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "meta-campaign-detail"] });
      toast({ variant: "success", title: "Ad duplicated — the copy is paused" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to duplicate ad" } });
    },
  });
}

export function useAdPreviews(
  adId: string | null,
  adFormat: string,
  creativeFeature?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "ad-previews", adId, adFormat, creativeFeature],
    queryFn: async () => {
      if (!adId || !adFormat) return null;
      const res = await adsApi.getAdPreviews(adId, {
        ad_format: adFormat,
        ...(creativeFeature ? { creative_feature: creativeFeature } : {}),
      });
      return res.data;
    },
    enabled: !!adId && !!adFormat && enabled,
    staleTime: 60 * 1000,
  });
}

export function useCreateInsightsReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      object_id: string;
      level?: string;
      date_preset?: string;
      time_range?: any;
      breakdowns?: string;
      fields?: string;
    }) => {
      const res = await adsApi.createInsightsReport(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create insights report" } });
    },
  });
}

export function useInsightsReport(reportRunId: string | null) {
  return useQuery({
    queryKey: ["ads", "insights-report", reportRunId],
    queryFn: async () => {
      if (!reportRunId) return null;
      const res = await adsApi.pollInsightsReport(reportRunId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!reportRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      if (isMetaRateLimitError(query.state.error)) return false;
      return 3000;
    },
    retry: (count, error) => shouldRetryMetaQuery(count, error, 2),
    staleTime: 0,
  });
}

export function useBroadTargetingCategories(enabled = false) {
  return useQuery({
    queryKey: ["ads", "broad-targeting-categories"],
    queryFn: async () => {
      const res = await adsApi.getBroadTargetingCategories();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAdPixels(enabled = false) {
  return useQuery({
    queryKey: ["ads", "pixels"],
    queryFn: async () => {
      const res = await adsApi.getAdPixels();
      return res.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: "always",
  });
}

export function useCreateAdPixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; business_account_id?: string | null; connect_existing?: boolean }) => {
      const res = await adsApi.createAdPixel(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create pixel");
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "pixels"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "crm-dataset"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "dataset-quality"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
    },
  });
}

export function useCapiDatasets(enabled = false) {
  return useQuery({
    queryKey: ["ads", "capi-datasets"],
    queryFn: async () => {
      const res = await adsApi.getCapiDatasets();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomConversions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "custom-conversions"],
    queryFn: async () => {
      const res = await adsApi.getCustomConversions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePagePosts(enabled = false) {
  return useQuery({
    queryKey: ["ads", "page-posts"],
    queryFn: async () => {
      const res = await adsApi.getPagePosts(25);
      const payload = res.data;
      if (Array.isArray(payload)) {
        return { pages: [], posts: payload, warning: null };
      }
      return {
        pages: payload?.pages || [],
        posts: payload?.posts || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstagramMedia(enabled = false) {
  return useQuery({
    queryKey: ["ads", "instagram-media"],
    queryFn: async () => {
      const res = await adsApi.getInstagramMedia(25);
      const payload = res.data;
      if (Array.isArray(payload)) {
        return { accounts: [], media: payload, warning: null };
      }
      return {
        accounts: payload?.accounts || [],
        media: payload?.media || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadVideoByUrl() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { video_url: string; title?: string }) => {
      const res = await adsApi.uploadVideoByUrl(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Video upload failed" } });
    },
  });
}

const videoPollStartedAt = new Map<string, number>();

export function useAdVideoStatus(videoId: string | null) {
  const query = useQuery({
    queryKey: ["ads", "video-status", videoId],
    queryFn: async () => {
      if (!videoId) return null;
      if (!videoPollStartedAt.has(videoId)) videoPollStartedAt.set(videoId, Date.now());
      const res = await adsApi.getVideoStatus(videoId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data as { id: string; videoStatus: string | null; errorMessage?: string | null } | null;
    },
    enabled: !!videoId,
    refetchInterval: (q) => {
      const status = (q.state.data as any)?.videoStatus;
      if (isTerminalVideoStatus(status)) return false;
      if (q.state.status === "error") return false;
      if (videoId && videoPollExpired(videoPollStartedAt.get(videoId))) return false;
      return VIDEO_POLL_INTERVAL_MS;
    },
    retry: (count, err: unknown) => shouldRetryMetaQuery(count, err, 2),
  });
  const status = query.data?.videoStatus ?? null;
  const pollState = videoId
    ? videoPollState({
        status,
        checkFailed: query.status === "error",
        startedAt: videoPollStartedAt.get(videoId) ?? null,
      })
    : null;
  return { ...query, pollState, problem: pollState ? videoProblemMessage(pollState, query.data?.errorMessage) : null };
}

export function useAdImageDetails(hash: string | null) {
  const valid = !!hash && /^[a-f0-9]{32}$/i.test(hash);
  return useQuery({
    queryKey: ["ads", "image-details", hash],
    queryFn: async () => {
      const res = await adsApi.getAdImageDetails(hash as string);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as { hash: string; name: string | null; width: number | null; height: number | null; created_time: string | null } | null;
    },
    enabled: valid,
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });
}

export function useAdVideoMedia(videoId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["ads", "video-media", videoId],
    queryFn: async () => {
      if (!videoId) return null;
      const res = await adsApi.getVideoMedia(videoId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as {
        id: string;
        picture: string | null;
        source: string | null;
        title?: string | null;
        file_size?: number | null;
        created_time?: string | null;
        length?: number | null;
      } | null;
    },
    enabled: !!videoId && enabled,
    staleTime: 30 * 60 * 1000,
    retry: 0,
  });
}

export interface AdRecommendation {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  post_stats?: {
    reach?: number;
    engagement?: number;
    like_count?: number;
    comment_count?: number;
    share_count?: number;
    saved?: number;
    engagement_rate?: number;
  };
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  cost_per_lead_low?: number;
  cost_per_lead_high?: number;
  target_audience?: {
    age_range?: string;
    gender?: string;
    interests?: string[];
    location_type?: string;
  };
  reason?: string;
  token_usage?: {
    model?: string;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    estimated_cost_usd?: number;
    total_cost_inr?: number;
    charged_cost_inr?: number;
  };
}

export function useAdRecommendation() {
  return useMutation({
    mutationFn: async (data: {
      post?: Record<string, any>;
      baseline?: Record<string, any>;
      image_url?: string;
      business_account_id?: string;
    }): Promise<AdRecommendation> => {
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/suggest-from-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post: data.post || {},
          baseline: data.baseline || null,
          business_account_id: data.business_account_id || null,
          organization_id: orgId || null,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw ApiError.fromResult(json, 'Failed to get recommendation');
      }
      return json as AdRecommendation;
    },
  });
}

export function useCreateAdFromPost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      source: { page_post_id?: string; ig_media_id?: string };
      name?: string;
      objective?: string;
      destination?: string;
      lead_form_id?: string;
      daily_budget?: number;
      targeting_spec?: any;
      business_account_id?: string;
    }) => {
      const res = await adsApi.createAdFromPost(data);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to create ad from post");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success",
        title: "Ad created (paused)",
        description: "Review it in the Ads page, then activate to start delivery.",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Couldn't create the ad" } });
    },
  });
}

export interface AttributionRow {
  meta_ad_id: string | null;
  ad_name: string | null;
  leads: number;
  meetings: number;
  sales: number;
  revenue: number;
  currency: string;
  source: string;
}

export function useAdAttribution(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "attribution", params],
    queryFn: async (): Promise<AttributionRow[]> => {
      const res = await adsApi.getAttribution(params);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load attribution");
      }
      return (res.data as AttributionRow[]) || [];
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type { CrmLeadQualityRow } from "@/lib/ads/leadQuality";

export function useAdsLeadQuality(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "lead-quality", params],
    queryFn: async (): Promise<import("@/lib/ads/leadQuality").CrmLeadQualityRow[]> => {
      const res = await adsApi.getLeadQuality(params);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return (res.data as import("@/lib/ads/leadQuality").CrmLeadQualityRow[]) || [];
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export function useAdRankings(
  win: { datePreset?: string; range?: { since: string; until: string } | null; adIds?: Array<string | number> },
  enabled = true,
) {
  const setupStatus = useAdsSetupStatus();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId ?? null;
  const accountKey: string | null = setupStatus.data?.account?.ad_account_id ?? null;
  const connected = !!setupStatus.data?.connected;
  const rangeKey = win.range?.since && win.range?.until ? `${win.range.since}:${win.range.until}` : null;
  const adIdsKey = win.adIds?.length ? [...new Set(win.adIds.map(String))].sort().join(",") : "";
  return useQuery({
    queryKey: ["ads", "rankings", workspaceId, accountKey, win.datePreset ?? null, rangeKey, adIdsKey],
    queryFn: async (): Promise<import("@/lib/ads/adRankings").AdRankingsResponse> => {
      const res = await adsApi.getAdRankings({
        ...(win.datePreset ? { date_preset: win.datePreset } : {}),
        ...(win.range?.since && win.range?.until ? { since: win.range.since, until: win.range.until } : {}),
        ...(adIdsKey ? { ad_ids: adIdsKey } : {}),
      });
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as import("@/lib/ads/adRankings").AdRankingsResponse;
    },
    enabled: enabled && connected && !!accountKey,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 0,
  });
}

export type CrmLeadStageLead = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  type: string | null;
  created_at: string | null;
};

export type CrmLeadStageBucket = {
  stage_id: string | null;
  stage_name: string;
  stage_color: string | null;
  board_name: string | null;
  count: number;
  is_unassigned: boolean;
  leads?: CrmLeadStageLead[];
  leads_truncated?: boolean;
};

export type CrmLeadStageBreakdown = {
  ad_id: string | null;
  ad_name: string | null;
  match_key: string;
  crm_leads: number;
  stages: CrmLeadStageBucket[];
};

export function useAdsLeadQualityStages(
  params: { adId?: string | null; adName?: string | null; from?: string; to?: string },
  enabled = false
) {
  return useQuery({
    queryKey: ["ads", "lead-quality-stages", params],
    queryFn: async (): Promise<CrmLeadStageBreakdown | null> => {
      try {
        const res = await adsApi.getLeadQualityStages({
          ad_id: params.adId || undefined,
          ad_name: params.adName || undefined,
          from: params.from,
          to: params.to,
        });
        if (res.error || res.success === false) return null;
        return (res.data as CrmLeadStageBreakdown) || null;
      } catch {
        return null;
      }
    },
    enabled:
      enabled &&
      !!(
        (params.adId && String(params.adId).trim()) ||
        (params.adName && String(params.adName).trim())
      ),
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export interface AdSuggestion {
  id: string;
  ig_media_id: string | null;
  page_post_id: string | null;
  caption: string;
  thumbnail_url: string | null;
  media_url?: string | null;
  like_count?: number;
  comment_count?: number;
  multiplier: number;
  metric: string;
  recommended_objective: string;
  recommended_budget: number;
  estimated_leads_low: number;
  estimated_leads_high: number;
  reason?: string;
  status: "pending" | "dismissed" | "promoted";
}

export interface PostPromoteSuggestion {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  reason: string;
  ai_source?: string;
}

export function useSuggestFromPost() {
  return useMutation({
    mutationFn: async (vars: {
      post: Record<string, any>;
      baseline?: Record<string, any>;
      business_account_id?: string;
    }): Promise<PostPromoteSuggestion> => {
      const res = await adsApi.suggestFromPost(vars);
      if (res.error || res.success === false) {
        return { should_promote: false, reason: "Suggestion unavailable right now.", ai_source: "unavailable" };
      }
      return res.data as PostPromoteSuggestion;
    },
  });
}

export function useAdSuggestions(enabled = true) {
  return useQuery({
    queryKey: ["ads", "suggestions-live"],
    queryFn: async (): Promise<AdSuggestion[]> => {
      const accountsRes = await apiClient.get('/instagram/accounts');
      const accounts: any[] = accountsRes?.data || [];
      if (accounts.length === 0) return [];

      const allPosts: any[] = [];
      await Promise.all(
        accounts.map(async (acc: any) => {
          try {
            const res = await apiClient.get('/instagram/automation/posts', {
              params: { instagram_account_id: acc.id, limit: 25 },
            });
            const posts: any[] = res?.data || [];
            allPosts.push(...posts);
          } catch {
          }
        })
      );

      if (allPosts.length < 3) return [];

      const engagements = allPosts.map((p: any) => (Number(p.like_count || 0) + Number(p.comment_count || 0)));
      const avgEngagement = engagements.reduce((a: number, b: number) => a + b, 0) / engagements.length;

      const payloadPosts = allPosts.map((p: any) => ({
        ig_media_id: p.ig_post_id || p.id,
        caption: p.caption || "",
        thumbnail_url: p.thumbnail_url || p.media_url || null,
        engagement: Number(p.like_count || 0) + Number(p.comment_count || 0),
        like_count: Number(p.like_count || 0),
        comment_count: Number(p.comment_count || 0),
      }));

      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/rank-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          posts: payloadPosts,
          baseline: { avg_engagement: Math.round(avgEngagement * 100) / 100 },
          organization_id: orgId || null,
        }),
      });

      if (!resp.ok) return [];
      const json = await resp.json();
      const suggestions: any[] = json?.suggestions || [];

      return suggestions
        .filter((s: any) => s.should_promote)
        .map((s: any, idx: number) => {
          const post = allPosts[s.index ?? idx];
          return {
            id: post?.ig_post_id || post?.id || `suggestion-${idx}`,
            ig_media_id: post?.ig_post_id || post?.id || null,
            page_post_id: null,
            caption: post?.caption || "",
            thumbnail_url: post?.thumbnail_url || post?.media_url || null,
            media_url: post?.media_url || null,
            like_count: Number(post?.like_count || 0),
            comment_count: Number(post?.comment_count || 0),
            multiplier: Number(s.multiplier || 1),
            metric: s.metric || "engagement",
            recommended_objective: s.recommended_objective || "OUTCOME_LEADS",
            recommended_budget: Number(s.recommended_budget || 500),
            estimated_leads_low: Number(s.estimated_leads_low || 0),
            estimated_leads_high: Number(s.estimated_leads_high || 0),
            reason: s.reason || "",
            status: "pending" as const,
          };
        });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useDismissAdSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adsApi.dismissSuggestion(id);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to dismiss suggestion");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suggestions"] });
    },
  });
}

export { adsApi };

export function useCrmStageMapping() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  return useQuery<CrmStageMappingView>({
    queryKey: ["ads", "crm-stage-mapping", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getCrmStageMapping(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as CrmStageMappingView;
    },
    enabled: !!(businessAccountId || workspaceId),
    staleTime: 30 * 1000,
    retry: 0,
  });
}

export function useUpdateCrmStageMapping() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (mappings: CrmStageMappingInput[]) => {
      const res = await adsApi.updateCrmStageMapping(businessAccountId as string, mappings);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ads", "crm-stage-mapping", businessAccountId],
      });
      toast({ title: "Stage mapping saved" });
    },
    onError: (err) => showApiError(err, toast, "Could not save the stage mapping"),
  });
}

export function useUpdateRawLeadEvent() {
  const queryClient = useQueryClient();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const queryKey = ["ads", "crm-stage-mapping", businessAccountId, workspaceId];
  return useMutation({
    mutationFn: async (input: { adAccountId: string; enabled: boolean }) => {
      const res = await adsApi.setRawLeadEvent(bareAdAccountId(input.adAccountId), input.enabled);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CrmStageMappingView>(queryKey);
      if (previous?.raw_lead_event) {
        const id = bareAdAccountId(input.adAccountId);
        queryClient.setQueryData<CrmStageMappingView>(queryKey, {
          ...previous,
          raw_lead_event: {
            ...previous.raw_lead_event,
            accounts: previous.raw_lead_event.accounts.map((acc) =>
              bareAdAccountId(acc.ad_account_id) === id ? { ...acc, enabled: input.enabled } : acc
            ),
          },
        });
      }
      return { previous };
    },
    onError: (err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      showApiError(err, { fallback: { title: "Could not change the new-lead event" } });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "crm-stage-mapping", businessAccountId] });
    },
  });
}

export function useCrmDataset() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  return useQuery<CrmDatasetView>({
    queryKey: ["ads", "crm-dataset", businessAccountId, workspaceId],
    queryFn: async () => {
      const res = await adsApi.getCrmDataset(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as CrmDatasetView;
    },
    enabled: !!(businessAccountId || workspaceId),
    staleTime: 30 * 1000,
    retry: 0,
  });
}

function useDatasetQualityScope() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const accountKey = useSelectedAdAccountId();
  return {
    businessAccountId,
    workspaceId,
    queryKey: ["ads", "dataset-quality", businessAccountId, workspaceId, accountKey] as const,
  };
}

export function useDatasetQuality() {
  const { businessAccountId, workspaceId, queryKey } = useDatasetQualityScope();
  return useQuery<DatasetQualityView>({
    queryKey,
    queryFn: async () => {
      const res = await adsApi.getDatasetQuality(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as DatasetQualityView;
    },
    enabled: !!(businessAccountId || workspaceId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useRefreshDatasetQuality() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { businessAccountId, queryKey } = useDatasetQualityScope();
  const mutation = useMutation({
    mutationFn: async (vars: { businessAccountId: string | null | undefined; queryKey: readonly unknown[] }) => {
      const res = await adsApi.getDatasetQuality(vars.businessAccountId, true);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as DatasetQualityView;
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(vars.queryKey, stampRefreshAvailability(data));
      const notice = refreshNotice(data?.refresh);
      if (notice) toast(notice);
    },
    onError: (err, vars) => {
      if (isMetaRateLimitError(err)) {
        const seconds = retryAfterSecondsFrom(err) ?? 60;
        queryClient.setQueryData<DatasetQualityView>(vars.queryKey, (current) =>
          current
            ? stampRefreshAvailability({ ...current, refresh: { status: "rate_limited", retry_after_seconds: seconds } })
            : current,
        );
      }
      showApiError(err, { fallback: { title: "Could not refresh data quality" } });
    },
  });
  return {
    isPending: mutation.isPending,
    refresh: () => mutation.mutate({ businessAccountId, queryKey }),
  };
}

export interface DatasetEvent {
  name: string;
  received_28d: number;
  sent_by_wenext: number;
  last_sent_at: string | null;
  in_meta_quality: boolean;
}

export interface DatasetEventsView {
  dataset_id: string | null;
  dataset_name?: string | null;
  is_crm?: boolean | null;
  state: "ready" | "no_events" | "no_dataset" | "reconnect" | "unavailable";
  events: DatasetEvent[];
  partial?: string[] | null;
  personal_use?: boolean | null;
}

export function useDatasetEvents(datasetId: string | null | undefined, enabled = true) {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const accountKey = useSelectedAdAccountId();
  return useQuery<DatasetEventsView>({
    queryKey: ["ads", "dataset-events", businessAccountId, workspaceId, accountKey, datasetId ?? null],
    queryFn: async () => {
      const res = await adsApi.getDatasetEvents(businessAccountId, datasetId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as DatasetEventsView;
    },
    enabled: enabled && !!datasetId && !!(businessAccountId || workspaceId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useActiveAdAccountDatasetId(): string | null {
  const setup = useAdsSetupStatus();
  const account = (setup.data as { account?: { dataset_id?: string | null } | null } | undefined)?.account;
  return account?.dataset_id ? String(account.dataset_id) : null;
}

function useInstantFormsScope() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const accountKey = useSelectedAdAccountId();
  return {
    businessAccountId,
    workspaceId,
    queryKey: ["ads", "instant-forms", businessAccountId, workspaceId, accountKey] as const,
  };
}

export function useValueRuleSets(enabled = true) {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  const workspaceId = useWorkspaceContext().selectedWorkspaceId;
  const accountKey = useSelectedAdAccountId();
  return useQuery<ValueRuleSetsResponse>({
    queryKey: ["ads", "value-rule-sets", businessAccountId, workspaceId, accountKey],
    queryFn: async () => {
      const res = await adsApi.getValueRuleSets(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as ValueRuleSetsResponse;
    },
    enabled: enabled && !!(businessAccountId || workspaceId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useCreateValueRuleSet() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await adsApi.createValueRuleSet({ ...body, ...(businessAccountId ? { business_account_id: businessAccountId } : {}) });
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as ValueRuleSetView;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "value-rule-sets"] });
      toast({ variant: "success", title: "Rule set created", description: "It is saved on your Meta ad account." });
    },
  });
}

export function useInstantForms(enabled = true) {
  const { businessAccountId, workspaceId, queryKey } = useInstantFormsScope();
  return useQuery<InstantFormsView>({
    queryKey,
    queryFn: async () => {
      const res = await adsApi.getInstantForms(businessAccountId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as InstantFormsView;
    },
    enabled: enabled && !!(businessAccountId || workspaceId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useRefreshInstantForms() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { businessAccountId, queryKey } = useInstantFormsScope();
  const mutation = useMutation({
    mutationFn: async (vars: { businessAccountId: string | null | undefined; queryKey: readonly unknown[] }) => {
      const res = await adsApi.getInstantForms(vars.businessAccountId, true);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data as InstantFormsView;
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(vars.queryKey, stampFormsRefresh(data));
      const notice = formsRefreshNotice(data?.refresh);
      if (notice) toast(notice);
    },
    onError: (err, vars) => {
      if (isMetaRateLimitError(err)) {
        const seconds = retryAfterSecondsFrom(err) ?? 60;
        queryClient.setQueryData<InstantFormsView>(vars.queryKey, (current) =>
          current ? stampFormsRefresh({ ...current, refresh: { status: "rate_limited", retry_after_seconds: seconds } }) : current,
        );
      }
      showApiError(err, { fallback: { title: "Could not refresh instant forms" } });
    },
  });
  return {
    isPending: mutation.isPending,
    refresh: () => mutation.mutate({ businessAccountId, queryKey }),
  };
}

export function useSetCrmDataset() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (crmDatasetId: string | null) => {
      const res = await adsApi.setCrmDataset(businessAccountId as string, crmDatasetId);
      if (res.error || res.success === false) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "crm-dataset", businessAccountId] });
      queryClient.invalidateQueries({ queryKey: ["ads", "dataset-quality"] });
      queryClient.invalidateQueries({
        queryKey: ["ads", "crm-stage-mapping", businessAccountId],
      });
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status", businessAccountId] });
      toast({ title: "CAPI dataset updated" });
    },
    onError: (err) => showApiError(err, toast, "Could not change the CAPI dataset"),
  });
}
